import * as XLSX from 'xlsx';
import { serverbaselink, aiserverbaselink } from '../../../Env_Variables';
import { aiInstituteHeaders } from './aiInstituteHeaders';

const jsonHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
};

/** Active abort signal while tools run (set by executeToolCalls). */
let toolAbortSignal = null;

const API_MAX_RETRIES = 3;
const RETRYABLE_HTTP = new Set([408, 429, 500, 502, 503, 504]);

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableFailure(err, status, data) {
    if (err?.name === 'AbortError') return false;
    if (status === 422) return false; // validation — caller may fix & retry
    if (status && RETRYABLE_HTTP.has(status)) return true;
    const msg = String(err?.message || err || '').toLowerCase();
    if (/failed to fetch|networkerror|network|timeout|econnreset|load failed/i.test(msg)) {
        return true;
    }
    const reason = String(data?.reason || data?.message || data?.error || data?.statusText || '').toLowerCase();
    if (
        data?.busy === true ||
        /busy|try again|too many|temporary|timeout|unavailable|overloaded|خدم|مشغول|حاول مرة|حاول تاني/.test(reason)
    ) {
        return true;
    }
    return false;
}

class ApiError extends Error {
    constructor(message, { status, data, path } = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
        this.path = path;
    }
}

async function postApi(path, body) {
    let lastError = null;

    for (let attempt = 0; attempt <= API_MAX_RETRIES; attempt += 1) {
        if (toolAbortSignal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }

        try {
            const response = await fetch(serverbaselink + path, {
                method: 'POST',
                credentials: 'include',
                headers: jsonHeaders,
                body: JSON.stringify(body || {}),
                signal: toolAbortSignal || undefined,
            });

            if (!response.ok) {
                let data = null;
                try {
                    data = await response.json();
                } catch (_) {
                    data = null;
                }
                const detail =
                    data?.message ||
                    data?.reason ||
                    (data?.errors ? JSON.stringify(data.errors) : '') ||
                    response.statusText;
                lastError = new ApiError(`API ${path} failed (${response.status})${detail ? `: ${detail}` : ''}`, {
                    status: response.status,
                    data,
                    path,
                });
                if (isRetryableFailure(lastError, response.status, data) && attempt < API_MAX_RETRIES) {
                    await sleep(400 * 2 ** attempt);
                    continue;
                }
                throw lastError;
            }

            const data = await response.json();
            if (isRetryableFailure(null, null, data) && attempt < API_MAX_RETRIES) {
                lastError = new ApiError(data?.reason || data?.message || `API ${path} busy`, {
                    status: 503,
                    data,
                    path,
                });
                await sleep(400 * 2 ** attempt);
                continue;
            }
            return data;
        } catch (err) {
            if (err?.name === 'AbortError' || toolAbortSignal?.aborted) {
                throw err;
            }
            lastError = err;
            if (!(err instanceof ApiError) && isRetryableFailure(err) && attempt < API_MAX_RETRIES) {
                await sleep(400 * 2 ** attempt);
                continue;
            }
            throw err;
        }
    }

    throw lastError || new Error(`API ${path} failed`);
}

/** Common Egyptian/Arabic given-name ↔ Latin spellings for customer search. */
const CUSTOMER_NAME_ALIASES = {
    محمود: ['mahmoud', 'mahmud', 'mahmod'],
    mahmoud: ['محمود'],
    mahmud: ['محمود'],
    أحمد: ['ahmed', 'ahmad'],
    احمد: ['ahmed', 'ahmad'],
    ahmed: ['أحمد', 'احمد'],
    ahmad: ['أحمد', 'احمد'],
    محمد: ['mohamed', 'mohammad', 'muhammad', 'mohammed'],
    mohamed: ['محمد'],
    mohammad: ['محمد'],
    muhammad: ['محمد'],
    mohammed: ['محمد'],
    علي: ['ali'],
    على: ['ali'],
    ali: ['علي', 'على'],
    يوسف: ['youssef', 'yousef', 'yusuf'],
    youssef: ['يوسف'],
    yousef: ['يوسف'],
    yusuf: ['يوسف'],
    سارة: ['sara', 'sarah'],
    sara: ['سارة'],
    sarah: ['سارة'],
    فاطمة: ['fatima', 'fatma'],
    fatima: ['فاطمة'],
    fatma: ['فاطمة'],
    عمر: ['omar'],
    omar: ['عمر'],
    خالد: ['khaled', 'khalid'],
    khaled: ['خالد'],
    khalid: ['خالد'],
    حسن: ['hassan', 'hasan'],
    hassan: ['حسن'],
    hasan: ['حسن'],
    حسين: ['hussein', 'hussain', 'hosein'],
    hussein: ['حسين'],
    إبراهيم: ['ibrahim'],
    ابراهيم: ['ibrahim'],
    ibrahim: ['إبراهيم', 'ابراهيم'],
    نور: ['nour', 'noor', 'nor'],
    nour: ['نور'],
    noor: ['نور'],
    مريم: ['mariam', 'maryam', 'mariam'],
    mariam: ['مريم'],
    maryam: ['مريم'],
    ياسر: ['yasser', 'yaser'],
    yasser: ['ياسر'],
    نسرين: ['nesreen', 'nisreen'],
    nesreen: ['نسرين'],
    nisreen: ['نسرين'],
};

const AR_TO_LATIN = {
    ا: 'a',
    أ: 'a',
    إ: 'i',
    آ: 'a',
    ب: 'b',
    ت: 't',
    ث: 'th',
    ج: 'g',
    ح: 'h',
    خ: 'kh',
    د: 'd',
    ذ: 'z',
    ر: 'r',
    ز: 'z',
    س: 's',
    ش: 'sh',
    ص: 's',
    ض: 'd',
    ط: 't',
    ظ: 'z',
    ع: 'a',
    غ: 'gh',
    ف: 'f',
    ق: 'k',
    ك: 'k',
    ل: 'l',
    م: 'm',
    ن: 'n',
    ه: 'h',
    و: 'w',
    ي: 'y',
    ى: 'a',
    ة: 'a',
    ء: '',
    ئ: 'y',
    ؤ: 'w',
};

const LATIN_TO_AR = {
    a: 'ا',
    b: 'ب',
    c: 'ك',
    d: 'د',
    e: 'ي',
    f: 'ف',
    g: 'ج',
    h: 'ه',
    i: 'ي',
    j: 'ج',
    k: 'ك',
    l: 'ل',
    m: 'م',
    n: 'ن',
    o: 'و',
    p: 'ب',
    q: 'ق',
    r: 'ر',
    s: 'س',
    t: 'ت',
    u: 'و',
    v: 'ف',
    w: 'و',
    x: 'كس',
    y: 'ي',
    z: 'ز',
};

function hasArabic(text) {
    return /[\u0600-\u06FF]/.test(text);
}

function hasLatinLetters(text) {
    return /[A-Za-z]/.test(text);
}

function transliterateArabicToLatin(text) {
    return String(text || '')
        .split('')
        .map((ch) => (Object.prototype.hasOwnProperty.call(AR_TO_LATIN, ch) ? AR_TO_LATIN[ch] : ch))
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
}

function transliterateLatinToArabic(text) {
    const lower = String(text || '').toLowerCase();
    let out = '';
    for (let i = 0; i < lower.length; i += 1) {
        const ch = lower[i];
        if (ch === ' ' || ch === '-' || ch === "'") {
            out += ' ';
            continue;
        }
        out += LATIN_TO_AR[ch] || ch;
    }
    return out.replace(/\s+/g, ' ').trim();
}

/**
 * Build EN+AR search variants for a customer name (spoken language may not match DB spelling).
 */
function buildCustomerNameVariants(query, extra = []) {
    const raw = String(query || '').trim();
    const variants = [];
    const push = (v) => {
        const s = String(v || '').trim();
        if (!s) return;
        if (!variants.some((x) => x.toLowerCase() === s.toLowerCase())) variants.push(s);
    };

    push(raw);
    for (const e of extra) push(e);

    const PRODUCT_PHRASE_ALIASES = {
        'بلاك شوز': ['black shoes', 'black shoe'],
        'بلاك شوذ': ['black shoes'],
        'black shoes': ['بلاك شوز', 'بلاك شوذ'],
        'black shoe': ['بلاك شوز'],
    };
    const phraseKey = raw.toLowerCase();
    const phraseAliases = PRODUCT_PHRASE_ALIASES[raw] || PRODUCT_PHRASE_ALIASES[phraseKey];
    if (phraseAliases) phraseAliases.forEach(push);

    const parts = raw.split(/\s+/).filter(Boolean);
    for (const part of parts) {
        const key = part.toLowerCase();
        const aliases = CUSTOMER_NAME_ALIASES[part] || CUSTOMER_NAME_ALIASES[key];
        if (aliases) aliases.forEach(push);
    }

    if (hasArabic(raw)) {
        const latin = transliterateArabicToLatin(raw);
        push(latin);
        latin.split(/\s+/).forEach((p) => {
            const aliases = CUSTOMER_NAME_ALIASES[p.toLowerCase()];
            if (aliases) aliases.forEach(push);
        });
    }

    if (hasLatinLetters(raw) && !hasArabic(raw)) {
        const arabic = transliterateLatinToArabic(raw);
        push(arabic);
        raw
            .toLowerCase()
            .split(/\s+/)
            .forEach((p) => {
                const aliases = CUSTOMER_NAME_ALIASES[p];
                if (aliases) aliases.forEach(push);
            });
    }

    return variants.slice(0, 6);
}

function clampLimit(value, fallback = 10) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return Math.min(Math.floor(n), 15);
}

function todayYmd(d = new Date()) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

/** Tool args only — never map user phrases like yesterday / next Friday. */
function asIsoDateOrAll(value) {
    const raw = String(value || '').trim();
    if (raw === 'all') return 'all';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    return null;
}

/** Tool args only — dashboard ser_time is HH:MM 24-hour (same as the time picker). */
function asApiSerTime(value) {
    if (value == null || value === '') return 'all';
    const raw = String(value).trim();
    if (raw === 'all') return 'all';
    let m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m) {
        const hour = Number(m[1]);
        if (hour >= 0 && hour <= 23) return `${String(hour).padStart(2, '0')}:${m[2]}`;
        return null;
    }
    m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)$/i);
    if (!m) return null;
    let hour = Number(m[1]);
    const min = m[2];
    const pm = m[3].toLowerCase() === 'pm';
    if (hour === 12) hour = pm ? 12 : 0;
    else if (pm) hour += 12;
    if (hour < 0 || hour > 23) return null;
    return `${String(hour).padStart(2, '0')}:${min}`;
}

async function resolveReservationProductIds(args = {}) {
    const passed = []
        .concat(args.product_ids || [], args.productid || [], args.product_id || [])
        .flatMap((v) => (Array.isArray(v) ? v : [v]))
        .map((v) => String(v || '').trim())
        .filter(Boolean);
    if (passed.length) return { ok: true, product_ids: [...new Set(passed)] };

    const query = String(args.product_query || args.session || args.session_query || '').trim();
    if (!query) return { ok: true, product_ids: [] };

    const found = await findProducts({ query, limit: 8 });
    const q = query.toLowerCase();
    const named = (found.products || []).filter((p) =>
        `${p.name || ''} ${p.name_en || ''} ${p.name_ar || ''}`.toLowerCase().includes(q),
    );
    const picks = named.length ? named : found.best_match ? [found.best_match] : [];
    const product_ids = [...new Set(picks.map((p) => String(p.productid || '')).filter(Boolean))];
    if (!product_ids.length) {
        return {
            ok: false,
            error: `No session/product matched "${query}".`,
            query,
            products: found.products || [],
        };
    }
    return {
        ok: true,
        product_ids,
        product_query: query,
        products: picks.map((p) => ({ productid: p.productid, name: p.name || p.name_en })),
    };
}

/** Match the asked service day on serfromdate (ser_date is often null). */
function reservationCoversDay(item, wantDay) {
    const start = String(item.serfromdate || item.ser_date || item.reservation_date || '').slice(0, 10);
    return Boolean(wantDay && start === wantDay);
}

function resolvePeriodRange(args = {}) {
    const period = args.period || 'this_month';
    const now = new Date();

    // Match dashboard analytics default (empty filter = all-time / no date filter)
    if (period === 'all' || period === 'all_time' || period === 'overall') {
        return { fromdate: null, todate: null, period: 'all' };
    }

    if (period === 'custom' && args.fromdate && args.todate) {
        return {
            fromdate: args.fromdate,
            todate: args.todate,
            period: 'custom',
        };
    }

    if (period === 'today') {
        const day = todayYmd(now);
        return { fromdate: day, todate: day, period };
    }

    if (period === 'this_week') {
        const day = new Date(now);
        const dow = day.getDay(); // 0 Sun
        const diffToMonday = dow === 0 ? -6 : 1 - dow;
        const start = new Date(day);
        start.setDate(day.getDate() + diffToMonday);
        return { fromdate: todayYmd(start), todate: todayYmd(now), period };
    }

    if (period === 'last_month') {
        const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const from = new Date(y, m, 1);
        const to = new Date(y, m + 1, 0);
        return { fromdate: todayYmd(from), todate: todayYmd(to), period };
    }

    // this_month (default for dated analytics)
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { fromdate: todayYmd(from), todate: todayYmd(now), period: 'this_month' };
}

function num(value) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
}

function firstAmount(...values) {
    for (const value of values) {
        if (value == null || value === '') continue;
        return value;
    }
    return '';
}

function computeOrderTotal(order, items) {
    const list = Array.isArray(items) ? items : [];
    let itemsTotal = 0;
    const existingItems = firstAmount(order?.totalOrderItemsFees, order?.totalitemsprice);
    if (existingItems !== '') {
        itemsTotal = num(existingItems);
    } else {
        for (const item of list) {
            if (item.total_item_price != null && item.total_item_price !== '') {
                itemsTotal += num(item.total_item_price);
            } else {
                itemsTotal += num(item.quantity || item.qty || 1) * num(item.finalprice || item.price);
            }
        }
    }
    const shipping = num(order?.zoneprice || order?.shippingfees || order?.shipping);
    const discount = num(order?.promocodediscountfees || order?.discountfees);
    const wallet = order?.usewalletpoints == 1 ? num(order?.walletpointsused) : 0;
    const extra = num(order?.orderextragenpayments?.totalvaluestobemodified);
    const total = itemsTotal - discount - wallet + shipping + extra;
    if (Number.isFinite(total) && (existingItems !== '' || list.length || shipping || extra)) {
        return String(Number(total.toFixed(2)));
    }
    const grand = firstAmount(order?.totalordersummary, order?.total, order?.ordertotal, order?.grandtotal);
    return grand !== '' ? String(grand) : '';
}

function pickOrderSummary(order) {
    const orderid = order.orderid || order.id || order.order_id;
    const customerid =
        order.customerid || order.customer_id || order.customerID || order.custid || order.userid || null;
    const firstLast = [order.firstname, order.lastname].filter(Boolean).join(' ').trim();
    return {
        orderid,
        ordernumber: order.ordernumber || order.order_number || order.orderno,
        customerid: customerid ? String(customerid) : null,
        customername: order.customername || order.customer_name || order.name || firstLast || '',
        paidstatus: order.paidstatus || order.paid_status,
        fullfilmstatus: order.fullfilmstatus || order.fulfillmentstatus || order.fullfilm_status,
        paymentmethod:
            order.paymentmethod ||
            order.payment_method ||
            order.paymethod ||
            order.paymenttype ||
            order.payment ||
            '',
        total: computeOrderTotal(order, order.orderitems || order.items),
        created_at:
            order.timestamp ||
            order.created_at ||
            order.createdat ||
            order.orderdate ||
            order.date,
        open_path: `/dashboard/institute/orders/showorder/${orderid}`,
        customer_open_path: customerid
            ? `/dashboard/institute/customer/showcustomer/${customerid}`
            : null,
    };
}

function pickProductSummary(product) {
    const productid = product.productid || product.id;
    return {
        productid,
        name: product.productname || product.name || product.title,
        price: product.price || product.productprice,
        quantity: product.quantity || product.qty || product.stock,
        active: product.productactive ?? product.active,
        open_path: productInfoPath(productid, 'information'),
    };
}

/** Product info page tabs (must match Productinfo.js slugs). */
function productInfoPath(productid, section = 'information') {
    const id = String(productid || '').trim();
    if (!id) return '/dashboard/institute/products/all/0';
    const slug = String(section || 'information').trim() || 'information';
    return `/dashboard/institute/products/productinfo/${id}?section=${encodeURIComponent(slug)}`;
}

function pickReservationSummary(item) {
    return {
        orderid: item.orderid || item.order_id,
        productname: item.productname || item.name || item.itemname,
        customername: item.customername || item.customer_name,
        ser_date: item.ser_date || item.serfromdate || item.reservation_date,
        serfromdate: item.serfromdate || item.ser_date || item.reservation_date,
        settodate: item.settodate || null,
        ser_time: item.ser_time || item.sertime || item.reservation_time,
        quantity: item.quantity || item.qty,
    };
}

function pickCustomerSummary(customer) {
    return {
        customerid: customer.customerid || customer.id,
        name: customer.customername || customer.name || `${customer.firstname || ''} ${customer.lastname || ''}`.trim(),
        phone: customer.phone || customer.mobilenumber || customer.mobile,
        email: customer.email,
    };
}

async function getOrders(args = {}) {
    const limit = clampLimit(args.limit);
    const fromdate = args.fromdate || '';
    const todate = args.todate || '';

    const data = await postApi('/instowner/fetchorders', {
        page: '0',
        functype: 'allorders',
        pagination: '0',
        customerstatus: 'all',
        staffstatus: 'all',
        fullfilmstatus: 'all',
        paidstatus: 'all',
        archivestatus: 'all',
        courier: 'all',
        customer_id: '',
        fromdate,
        todate,
    });

    const orders = data.orders || data.data?.orders || [];
    const summarized = orders.slice(0, limit).map(pickOrderSummary);
    return {
        ok: Boolean(data.status ?? true),
        count: data.orderscount ?? data.total ?? orders.length,
        fromdate: fromdate || null,
        todate: todate || null,
        latest_orderid: summarized[0]?.orderid || null,
        latest_open_path: summarized[0]?.open_path || null,
        orders: summarized,
        note: 'For full details of one order (customer, items, totals), use get_order_info with orderid. Speak those fields aloud — do not only open the page.',
    };
}

async function getOrderInfo(args = {}) {
    let orderid = String(args.orderid || args.order_id || '').trim();

    if (!orderid && (args.latest || args.last)) {
        const latest = await getOrders({ limit: 1, fromdate: '', todate: '' });
        orderid = String(latest.latest_orderid || '').trim();
    }

    if (!orderid) {
        return { ok: false, error: 'orderid is required (or pass latest=true)' };
    }

    const data = await postApi('/instowner/fetchorderinformation', { orderid });
    const payload = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
    const order = payload.orderinfo || payload.order || payload;
    const items =
        order.orderitems ||
        order.items ||
        payload.orderitems ||
        payload.items ||
        [];

    const itemSummaries = (Array.isArray(items) ? items : []).slice(0, 12).map((item) => ({
        name: item.productname || item.name || item.itemname || item.title,
        quantity: item.quantity || item.qty || 1,
        price: item.total_item_price || item.finalprice || item.price,
    }));
    const orderTotal = computeOrderTotal(order, items);

    // API already returns customer main info (flat on order payload and/or customerinfo/customer object).
    const customerObj =
        (payload.customerinfo && typeof payload.customerinfo === 'object' ? payload.customerinfo : null) ||
        (payload.customer && typeof payload.customer === 'object' ? payload.customer : null) ||
        (order.customerinfo && typeof order.customerinfo === 'object' ? order.customerinfo : null) ||
        (order.customer && typeof order.customer === 'object' ? order.customer : null) ||
        {};

    const customerid = String(
        customerObj.customerid ||
            customerObj.id ||
            order.customerid ||
            order.customer_id ||
            payload.customerid ||
            payload.customer_id ||
            '',
    ).trim();

    const customer = {
        customerid: customerid || null,
        name:
            customerObj.name ||
            customerObj.customername ||
            order.customername ||
            payload.customername ||
            [order.firstname, order.lastname].filter(Boolean).join(' ') ||
            '',
        phone:
            customerObj.mobile ||
            customerObj.phone ||
            customerObj.mobilenumber ||
            order.customerphonenumber ||
            order.phone ||
            order.mobilenumber ||
            order.customerphone ||
            payload.customerphonenumber ||
            '',
        email:
            customerObj.email ||
            order.customeremail ||
            order.email ||
            payload.customeremail ||
            '',
        address:
            customerObj.shippingaddress ||
            customerObj.address ||
            order.address ||
            order.customeraddress ||
            order.fulladdress ||
            payload.address ||
            '',
        open_path: customerid ? `/dashboard/institute/customer/showcustomer/${customerid}` : null,
    };

    return {
        ok: Boolean(payload.status ?? data.status ?? true),
        orderid,
        customerid: customer.customerid,
        customer,
        customer_open_path: customer.open_path,
        speak_aloud: true,
        note: 'YOU MUST speak these details out loud to the user. Do not say "look at the page" instead of reading them.',
        follow_up: customer.customerid
            ? `If the user asks about this/that/the customer, call get_customer_info with customerid="${customer.customerid}" from customer.customerid — do NOT search by name.`
            : 'If customerid is missing and user asks about the customer, use get_customer_info with phone from customer.phone first, then name only if needed.',
        summary: {
            orderid,
            customerid: customer.customerid,
            customername: customer.name,
            phone: customer.phone,
            email: customer.email,
            paidstatus: order.paidstatus || payload.paidstatus,
            paymentmethod:
                order.paymentmethod ||
                order.payment_method ||
                payload.paymentmethod ||
                payload.payment_method ||
                '',
            fulfillment: order.fullfilmstatus || order.isfulfilled || payload.isfulfilled,
            ordermainstatus: order.ordermainstatus || payload.ordermainstatus,
            total: orderTotal,
            shipping: order.zoneprice || order.shippingfees || order.shipping,
            address: customer.address,
            city: order.city || order.customercity || customerObj.city || '',
            items_count: itemSummaries.length,
            items: itemSummaries,
        },
        open_path: `/dashboard/institute/orders/showorder/${orderid}`,
        ...slimApi({ orderid }, payload),
    };
}

async function getProducts(args = {}) {
    const limit = clampLimit(args.limit);
    const payload = {
        filter: 0,
        collectionid: 'all',
        vendorid: 'all',
        hassale: 'all',
        productactive: 'all',
        page: 0,
        outofstock: args.outofstock || 'all',
        productindex_sortby: '',
        hasvariants: 'all',
        features: [],
    };
    if (args.search) payload.search = args.search;

    const data = await postApi('/instowner/fetchproducts', payload);
    const products = data.products || data.data?.products || [];

    return {
        ok: Boolean(data.status ?? true),
        count: data.allproductscount ?? data.productscount ?? products.length,
        products: products.slice(0, limit).map(pickProductSummary),
    };
}

function firstNonEmptyArray(...candidates) {
    for (const c of candidates) {
        if (Array.isArray(c) && c.length > 0) return c;
    }
    for (const c of candidates) {
        if (Array.isArray(c)) return c;
    }
    return [];
}

async function getReservations(args = {}) {
    const ser_date = asIsoDateOrAll(args.ser_date);
    if (!ser_date) {
        return {
            ok: false,
            error:
                'ser_date must be YYYY-MM-DD (or "all"). Compute it from today in the system prompt — do not pass words like yesterday/today/tomorrow.',
            got: args.ser_date == null ? null : String(args.ser_date),
        };
    }
    const ser_time = asApiSerTime(args.ser_time);
    if (!ser_time) {
        return {
            ok: false,
            error: 'ser_time must be HH:MM 24-hour (8:15 AM → 08:15) or "all".',
            got: args.ser_time == null ? null : String(args.ser_time),
        };
    }
    const wantDay = ser_date === 'all' ? null : ser_date;
    const products = await resolveReservationProductIds(args);
    if (!products.ok) return products;

    // One Fetch_Order_Items call. ser_date / ser_time / product_ids match the reservations screen.
    const filterobj = {
        functypeparam: 'allorders',
        paginationparam: '0',
        itemtype: 'reservations',
        groupbyskus: 'all',
        fromdate: 'all',
        todate: 'all',
        ser_date: wantDay || 'all',
        ser_time,
        vendorid: args.vendorid || 'all',
        product_ids: products.product_ids,
    };
    const data = await postApi('/instowner/Fetch_Order_Items', {
        page: '0',
        filterobj,
    });
    const rows = firstNonEmptyArray(
        data.reservations,
        data.orderitems,
        data.items,
        data.orders,
        data.data?.reservations,
        data.data?.orderitems,
        data.data?.items,
        data.data?.orders,
    );
    const payload =
        data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
    const collected = rows.map((item) => {
        const summary = pickReservationSummary(item);
        return {
            ...summary,
            matches_asked_day: wantDay ? reservationCoversDay(item, wantDay) : true,
        };
    });
    const lastpage = Number(
        payload.lastpage ?? payload.totalpages ?? payload.pagescount ?? payload.pagecount ?? data.lastpage,
    );
    const total = Number(
        payload.orderscount ??
            payload.total ??
            payload.itemscount ??
            payload.reservationscount ??
            payload.totalcount ??
            data.orderscount,
    );
    const perPage = Number(payload.numofitemsperpage) || 12;
    const pageField = payload.page ?? data.page;
    const hasPageField = pageField !== undefined && pageField !== null && pageField !== '';
    const hasMorePages =
        (hasPageField && collected.length >= perPage) ||
        (Number.isFinite(lastpage) && lastpage > 0) ||
        (Number.isFinite(total) && total > collected.length);

    return {
        ok: Boolean(data.status ?? true),
        source: 'Fetch_Order_Items',
        pages_fetched: 1,
        page: hasPageField ? pageField : 0,
        has_more_pages: hasMorePages,
        lastpage: Number.isFinite(lastpage) ? lastpage : null,
        total_count: Number.isFinite(total) ? total : null,
        count: collected.length,
        ser_date: wantDay || 'all',
        ser_time,
        requested_date: wantDay || 'all',
        product_ids: products.product_ids,
        product_query: products.product_query || null,
        matching_asked_day: collected.filter((r) => r.matches_asked_day).length,
        speak_aloud: true,
        note: hasMorePages
            ? `Table EVERY reservations[] row. These are the latest ${collected.length}. Do not fetch another page.`
            : 'Table EVERY reservations[] row. Do not fetch another page.',
        reservations: collected,
    };
}

async function getCustomers(args = {}) {
    const limit = clampLimit(args.limit);
    const data = await postApi('/instowner/fetchcustomers', {
        functype: 'all',
        branchid: 'all',
        pagenumbparams: 0,
    });

    const customers = data.customers || data.data?.customers || [];
    return {
        ok: Boolean(data.status ?? true),
        count: data.totalcustomerscount ?? data.customerscount ?? customers.length,
        customers: customers.slice(0, limit).map(pickCustomerSummary),
    };
}

async function searchCustomersOnce(query) {
    const data = await postApi('/instowner/searchcustomers', {
        customersearchquery: query,
    });

    const customers =
        data.customers ||
        data.data?.customers ||
        data.searchcustomers ||
        data.data?.searchcustomers ||
        data.result ||
        [];

    const list = Array.isArray(customers) ? customers : [];
    return {
        ok: Boolean(data.status ?? true),
        list,
    };
}

async function findCustomers(args = {}) {
    const query = String(args.query || '').trim();
    const limit = clampLimit(args.limit, 8);
    if (!query) {
        return { ok: false, error: 'query is required' };
    }

    const extra = []
        .concat(args.alt_query || [], args.query_en || [], args.query_ar || [], args.alt_queries || [])
        .flatMap((v) => (Array.isArray(v) ? v : [v]))
        .map((v) => String(v || '').trim())
        .filter(Boolean);

    const variants = buildCustomerNameVariants(query, extra);
    const byId = new Map();
    const tried = [];

    for (const variant of variants) {
        if (toolAbortSignal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        tried.push(variant);
        try {
            const { list } = await searchCustomersOnce(variant);
            for (const customer of list) {
                const customerid = customer.customerid || customer.id;
                if (!customerid || byId.has(String(customerid))) continue;
                byId.set(String(customerid), {
                    ...pickCustomerSummary(customer),
                    customerid,
                    matched_query: variant,
                    open_path: `/dashboard/institute/customer/showcustomer/${customerid}`,
                });
            }
        } catch (err) {
            if (err?.name === 'AbortError') throw err;
            // Continue other variants; postApi already retried transient failures.
            if (variants.length === 1) throw err;
        }
    }

    const matches = Array.from(byId.values()).slice(0, limit);

    return {
        ok: true,
        query,
        searched_variants: tried,
        count: matches.length,
        best_match: matches[0] || null,
        customers: matches,
        note:
            tried.length > 1
                ? 'Searched Arabic and English name variants. Prefer best_match; if several people, ask which customerid.'
                : undefined,
    };
}

function defaultCustomerDateRange() {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 3);
    return { from: todayYmd(from), to: todayYmd(to) };
}

function resolveCustomerDateRange(args = {}) {
    if (args.from && args.to) {
        return {
            from: args.from,
            to: args.to,
        };
    }
    if (args.period && args.period !== 'all') {
        const range = resolvePeriodRange(args);
        return { from: range.fromdate, to: range.todate };
    }
    return defaultCustomerDateRange();
}

function pickCustomerOrderSummary(order) {
    const orderid = order.orderid || order.id;
    const orderdate =
        order.timestamp ||
        order.orderdate ||
        order.created_at ||
        order.createdat ||
        order.date ||
        order.order_date ||
        order.ser_date ||
        '';
    return {
        orderid,
        orderdate,
        date: orderdate, // alias for the model
        timestamp: order.timestamp || orderdate,
        ordermainstatus: order.ordermainstatus || order.status,
        paidstatus: order.paidstatus || order.paid_status || order.orderrstatus || '',
        total:
            order.total ||
            order.ordertotal ||
            order.totalprice ||
            order.subtotal ||
            order.grandtotal ||
            order.finaltotal ||
            '',
        // Relative path only — never a full URL. Chat may link as [Order #id](path).
        path: `/dashboard/institute/orders/showorder/${orderid}`,
        open_path: `/dashboard/institute/orders/showorder/${orderid}`,
    };
}

async function getCustomerInfo(args = {}) {
    let customerid = args.customerid || args.customer_id || '';
    const nameQuery = String(args.query || args.name || '').trim();
    let searchMatches;

    if (!customerid && nameQuery) {
        const found = await findCustomers({
            query: nameQuery,
            alt_query: args.alt_query || args.query_en || args.query_ar,
            alt_queries: args.alt_queries,
            query_en: args.query_en,
            query_ar: args.query_ar,
            limit: 5,
        });
        if (!found.ok || !found.best_match?.customerid) {
            return {
                ok: false,
                error: 'Customer not found',
                query: nameQuery,
                searched_variants: found.searched_variants,
                customers: found.customers || [],
            };
        }
        if ((found.customers || []).length > 1) {
            // Prefer exact-ish name match ignoring script
            const q = nameQuery.toLowerCase();
            const exact = found.customers.filter((c) => {
                const n = String(c.customername || c.name || '').toLowerCase();
                return n === q || n.includes(q) || q.includes(n);
            });
            if (exact.length === 1) {
                customerid = exact[0].customerid;
                searchMatches = found.customers;
            } else {
                return {
                    ok: false,
                    needs_choice: true,
                    error: 'Multiple customers matched. Ask which customerid.',
                    query: nameQuery,
                    searched_variants: found.searched_variants,
                    customers: found.customers,
                };
            }
        } else {
            customerid = found.best_match.customerid;
            searchMatches = found.customers;
        }
    }

    if (!customerid) {
        return { ok: false, error: 'customerid or query is required' };
    }

    const range = resolveCustomerDateRange(args);
    const data = await postApi('/instowner/getcustomerinfo', {
        customerid: String(customerid),
        from: range.from,
        to: range.to,
    });

    const payload = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
    const customerinfo = payload.customerinfo || payload.customer || {};
    const orders = payload.orders || [];
    const open = orders.filter((o) => o.ordermainstatus == 'Open' || o.ordermainstatus === 'open').length;
    const canceled = orders.length - open;
    const spendingRows = payload.customerSpending || [];
    const totalSpent = spendingRows.reduce((sum, row) => sum + num(row.total_spent), 0);
    const mostBought = (payload.mostBoughtProducts || []).slice(0, 8).map((item) => ({
        productname: item.productname || item.sku || item.name,
        total_quantity: item.total_quantity || item.quantity,
    }));
    const ordersTrend = (payload.customerOrders || []).slice(-12).map((row) => ({
        date: row.date,
        total_orders: row.total_orders,
    }));

    const limit = clampLimit(args.limit, 10);
    const currency = await fetchDefaultCurrency();
    const currencyLabel =
        currency.currency_code || currency.currencyname_en || currency.currencyname_ar || '';

    return {
        ok: Boolean(payload.status ?? data.status ?? true),
        customerid: String(customerid),
        from: range.from,
        to: range.to,
        note: 'Date range max is typically ~3 months for this API; default is last 3 months. Call this tool ONCE then answer from analytics + recent_orders — do not re-fetch with other periods unless the user asks. recent_orders include orderdate/timestamp — ALWAYS show a Date column for recent orders (never invent dates or order ids). Follow-ups like "add dates to their recent orders" / "their orders" → use this same customerid again (NOT get_orders). Format answer as Markdown tables. Link orders with relative path only: [Order #id](/dashboard/institute/orders/showorder/id). Never invent http URLs. NEVER report 0 orders when analytics.orders_count > 0.',
        currency_label: currencyLabel,
        customer: {
            customerid: customerinfo.customerid || customerid,
            name: customerinfo.name || customerinfo.customername || '',
            phone: customerinfo.phone || customerinfo.mobilenumber || customerinfo.mobile || '',
            email: customerinfo.email || '',
        },
        matches: searchMatches,
        analytics: {
            orders_count: orders.length,
            open_orders: open,
            other_orders: canceled,
            services_count: payload.servicescount || 0,
            total_spent: totalSpent,
            total_spent_display: currencyLabel ? `${totalSpent} ${currencyLabel}` : String(totalSpent),
            most_bought_products: mostBought,
            orders_trend: ordersTrend,
        },
        recent_orders: orders.slice(0, limit).map(pickCustomerOrderSummary),
        open_path: `/dashboard/institute/customer/showcustomer/${customerid}`,
    };
}

function slugifyEmailLocal(name) {
    return (
        String(name || 'customer')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '.')
            .replace(/^\.+|\.+$/g, '')
            .slice(0, 40) || 'customer'
    );
}

function generateCustomerPassword() {
    const stamp = Date.now().toString(36);
    return `Tx${stamp}!9`;
}

function generateCustomerMobile() {
    const n = String(Date.now()).slice(-8);
    return `010${n}`;
}

/**
 * Create a customer. Name is enough.
 * country/state/city are NOT required — never fetch geo for this.
 * Optional: mobile, email, password, address (autofilled if missing).
 */
async function createCustomer(args = {}) {
    const name = String(args.name || args.customername || args.query || '').trim();
    if (!name) {
        return { ok: false, error: 'name is required (e.g. "Mohamed Riad")' };
    }

    const autofilled = [];
    let mobile = String(args.mobile || args.phone || '').trim();
    let email = String(args.email || '').trim();
    let password = String(args.password || '').trim();
    const shippingaddress = String(args.shippingaddress || args.address || '').trim();

    if (!mobile) {
        mobile = generateCustomerMobile();
        autofilled.push('mobile');
    }
    if (!email) {
        const stamp = Date.now().toString(36).slice(-4);
        email = `${slugifyEmailLocal(name)}.${stamp}@customer.local`;
        autofilled.push('email');
    }
    if (!password) {
        password = generateCustomerPassword();
        autofilled.push('password');
    }

    // Location is optional — send empty unless the user explicitly provided ids.
    const countryid = String(args.countryid || '').trim();
    const stateid = String(args.stateid || '').trim();
    const cityid = String(args.cityid || '').trim();

    let instsignupformvalues = [];
    try {
        const formData = await postApi('/instowner/fetchInstCustomersisgnupformdata', {});
        const signupformdata = formData.signupformdata || formData.data?.signupformdata || [];
        if (Array.isArray(signupformdata)) {
            instsignupformvalues = signupformdata.map((row) => ({ ...row }));
        }
    } catch (_) {
        /* optional */
    }

    const request = {
        functype: 'add',
        name,
        mobile,
        email,
        shippingaddress,
        instsignupformvalues,
        addedsourcetype: String(args.addedsourcetype || 'ai'),
        customerid: '',
        countryid,
        stateid,
        cityid,
        password,
        branchid: String(args.branchid || ''),
        isblocked: 0,
        isdeleted: 0,
        cancustomerviewpricesandaddtocart: Number(args.cancustomerviewpricesandaddtocart || 0),
        customermaxwallettobeusedperorder: args.customermaxwallettobeusedperorder || '',
    };

    const data = await postApi('/instowner/customerscrud', request);
    const payload = data.data && typeof data.data === 'object' ? data.data : data;

    let customerid =
        payload.customerid ||
        payload.newcustomerid ||
        payload.customer?.customerid ||
        data.customerid ||
        null;

    if (!customerid && (payload.status ?? data.status)) {
        try {
            await sleep(400);
            const found = await findCustomers({ query: mobile || name, limit: 5 });
            const match =
                (found.customers || []).find(
                    (c) =>
                        String(c.phone || c.mobile || '') === mobile ||
                        String(c.name || c.customername || '')
                            .toLowerCase()
                            .includes(name.toLowerCase()),
                ) || found.best_match;
            customerid = match?.customerid || null;
        } catch (_) {
            /* ignore */
        }
    }

    const open_path = customerid
        ? `/dashboard/institute/customer/showcustomer/${customerid}`
        : '/dashboard/institute/customers/all/all/0';

    return {
        ok: Boolean(payload.status ?? data.status) && Boolean(customerid || payload.status),
        created: Boolean(payload.status ?? data.status),
        customerid: customerid ? String(customerid) : null,
        customer: {
            customerid: customerid ? String(customerid) : null,
            name,
            mobile,
            email,
            shippingaddress,
        },
        autofilled,
        password_set: Boolean(password),
        reason: payload.reason || data.reason,
        open_path,
        navigate: { path: open_path, label: name },
        ...slimApi({ functype: 'add', name, mobile, email }, payload),
        note: (payload.status ?? data.status)
            ? customerid
                ? `Customer "${name}" created. Autofilled: ${autofilled.join(', ') || 'none'}. Open their profile. Never fetch cities/countries for customer create.`
                : `Customer create returned status=true but no customerid — check customers list.`
            : 'Create failed — see api_response.reason. Retry create_customer; do NOT open add-customer or fetch_geo.',
    };
}

async function fetchDefaultCurrency() {
    try {
        const data = await postApi('/instowner/fetchInstitutecurrency', {});
        const currencies = data.currencies || data.data?.currencies || [];
        const def =
            currencies.find((c) => c.isdefault == 1 || c.isdefault === '1' || c.currencyinfo?.isdefault == 1) ||
            currencies[0] ||
            null;
        if (!def) {
            return {
                currencyname_en: '',
                currencyname_ar: '',
                currency_code: '',
                currencyid: null,
            };
        }
        const info = def.currencyinfo || def;
        return {
            currencyid: def.currencyid || info.currencyid || null,
            currencyname_en: info.currencyname_en || def.currencyname_en || '',
            currencyname_ar: info.currencyname_ar || def.currencyname_ar || '',
            currency_code: info.currency_code || info.code || def.currency_code || info.currencyname_en || '',
            isdefault: true,
        };
    } catch (err) {
        return {
            currencyname_en: '',
            currencyname_ar: '',
            currency_code: '',
            error: err.message,
        };
    }
}

async function getSalesReport(args = {}) {
    // getreports requires from/to dates (unlike getPagesAnalytics).
    let range = resolvePeriodRange({
        ...args,
        period: args.period || 'this_month',
    });
    if (!range.fromdate || !range.todate) {
        const to = new Date();
        const from = new Date();
        from.setFullYear(from.getFullYear() - 5);
        range = {
            fromdate: todayYmd(from),
            todate: todayYmd(to),
            period: range.period || 'all',
        };
    }

    const vendoridparam = args.vendorid || args.vendoridparam || 'all';
    const courieridparam = args.courierid || args.courieridparam || 'all';

    const [data, currency] = await Promise.all([
        postApi('/instowner/getreports', {
            fromdateparam: range.fromdate,
            todateparam: range.todate,
            vendoridparam,
            courieridparam,
        }),
        fetchDefaultCurrency(),
    ]);

    const currencyLabel = currency.currencyname_en || currency.currency_code || '';
    const money = (value) => {
        const n = num(value);
        return {
            value: n,
            display: currencyLabel ? `${n.toLocaleString()} ${currencyLabel}` : String(n),
        };
    };

    // Summary cards (ReportsSummary.js)
    const revenue = money(data.totalordersprice);
    const cost = money(data.totalitems_cost_price);
    const profitRaw =
        data.grossProfit != null && data.grossProfit !== ''
            ? num(data.grossProfit)
            : revenue.value - cost.value;
    const profit = money(profitRaw);
    const margin =
        data.grossProfitMargin != null && data.grossProfitMargin !== ''
            ? num(data.grossProfitMargin)
            : revenue.value > 0
              ? ((revenue.value - cost.value) / revenue.value) * 100
              : 0;

    // Payment method collected values (cash / card / Agel / BNPL)
    const paymentMethodValuesRaw = data.paymentMethodValues ?? data.payment_method_values;
    const paymentMethodValuesObj = Array.isArray(paymentMethodValuesRaw)
        ? paymentMethodValuesRaw[0] || {}
        : paymentMethodValuesRaw || {};
    const paymentMethodValues = {
        cash: money(paymentMethodValuesObj.cash_value),
        online_payment: money(paymentMethodValuesObj.onlinepayment_value),
        agel: money(paymentMethodValuesObj.agel_value),
        bnpl: money(paymentMethodValuesObj.bnpl_value),
    };

    // Payment methods by order count + revenue
    const paymentMethods = (data.paymentMethods || []).map((row) => ({
        payment_method: row.paymentmethod || row.payment_method || 'Unknown',
        total_orders: num(row.total_orders),
        total_revenue: num(row.total_revenue),
        total_revenue_display: money(row.total_revenue).display,
    }));

    // Daily revenue / cost / profit trend
    const revenueCostProfit = (data.revenueCostProfit || []).slice(-31).map((row) => ({
        date: row.date,
        revenue: num(row.revenue),
        cost: num(row.cost),
        profit: num(row.profit),
    }));

    const vendorData = data.vendorData || null;
    const commission =
        vendoridparam !== 'all'
            ? {
                  comm_percent: num(vendorData?.comm_percent ?? data.comm_percent),
                  commission_value: money(data.commissionValue).value,
                  commission_value_display: money(data.commissionValue).display,
              }
            : null;

    return {
        ok: Boolean(data.status ?? true),
        source: 'getreports',
        period: range.period,
        fromdate: range.fromdate,
        todate: range.todate,
        vendorid: vendoridparam,
        courierid: courieridparam,
        currency,
        currency_label: currencyLabel,
        note:
            'PRIMARY sales/revenue/profit source (dashboard Reports). Answer ONLY from these fields — never invent. ' +
            'total_sales = revenue after discounts. cost_of_goods = product cost. gross_profit / gross_margin_percent. ' +
            'total_orders / total_customers / website_views / mobile_app_views. ' +
            'payment_method_values = cash/card/Agel/BNPL amounts. payment_methods = orders+revenue per method. ' +
            'revenue_cost_profit = daily revenue/cost/profit trend. Always use *_display / currency_label for money.',
        // Summary
        total_sales: revenue.value,
        total_sales_display: revenue.display,
        cost_of_goods: cost.value,
        cost_of_goods_display: cost.display,
        gross_profit: profit.value,
        gross_profit_display: profit.display,
        gross_margin_percent: Number(margin.toFixed(2)),
        total_orders: data.totalorders != null ? num(data.totalorders) : null,
        total_customers: data.totalcustomers != null ? num(data.totalcustomers) : null,
        website_views: num(data.views),
        mobile_app_views: num(data.mobviews),
        // Breakdowns
        payment_method_values: {
            cash: paymentMethodValues.cash.value,
            cash_display: paymentMethodValues.cash.display,
            online_payment: paymentMethodValues.online_payment.value,
            online_payment_display: paymentMethodValues.online_payment.display,
            agel: paymentMethodValues.agel.value,
            agel_display: paymentMethodValues.agel.display,
            bnpl: paymentMethodValues.bnpl.value,
            bnpl_display: paymentMethodValues.bnpl.display,
        },
        payment_methods: paymentMethods,
        revenue_cost_profit: revenueCostProfit,
        commission,
        reports_path: `/dashboard/institute/reports/${range.fromdate}/${range.todate}/${vendoridparam}/${courieridparam}/0`,
    };
}

function formatPeakHour(hour) {
    const h = num(hour);
    const hour12 = h % 12 || 12;
    const period = h >= 12 ? 'PM' : 'AM';
    return `${hour12} ${period}`;
}

function mapTopProductRows(rows, { mode = 'sold', limit = 10 } = {}) {
    // Names already come from getPagesAnalytics (product_name). Never fetchproductinfo here.
    return (rows || []).slice(0, limit).map((row) => {
        const productid = row.productid || row.id || null;
        const name = String(
            row.product_name || row.name || row.productname || row.itemname || '',
        ).trim();
        const base = {
            productid,
            name: name || (productid ? `Product ${productid}` : 'Unknown product'),
            open_path: productid ? productInfoPath(productid) : null,
        };
        if (mode === 'views') {
            return {
                ...base,
                totalviews: num(row.totalviews ?? row.views),
            };
        }
        const total_quantity = num(
            row.total_quantity ?? row.total_orders ?? row.quantity ?? row.qty,
        );
        return {
            ...base,
            total_quantity,
            total_orders: total_quantity,
        };
    });
}

async function getPagesAnalytics(args = {}) {
    // Default to all-time (empty filter) to match dashboard analytics when no dates set.
    const focus = String(args.focus || '').trim() || null;
    const range = resolvePeriodRange({
        ...args,
        period: args.period || 'all',
    });
    const payload = {};
    if (range.fromdate && range.todate) {
        payload.fromdate = range.fromdate;
        payload.todate = range.todate;
    }
    const data = await postApi('/instowner/getPagesAnalytics', payload);

    // Field map mirrors Graphs.js / getPagesAnalytics response:
    // ordersRate: order_date, total_orders
    // subtotalsRate: timestamp, total_value
    // topOrderedItems: productid, product_name, total_quantity
    // topOrderedCities: name, total_orders
    // topCustomers: customerid, customername, total_orders
    // topVisitedProducts: productid?, product_name, totalviews
    // customersRate: customer_date, total_customers
    // WebsiteVisitedRate / checkout_VisitedRate: date, totalviews
    // peakTimes: hour, visit_count

    const ordersRate = (data.ordersRate || []).slice(-21).map((row) => ({
        date: row.order_date || row.timestamp || row.date,
        total_orders: num(row.total_orders),
    }));

    const subtotalsRate = (data.subtotalsRate || []).slice(-21).map((row) => ({
        date: row.timestamp || row.order_date || row.date,
        total_value: num(row.total_value ?? row.total_subtotal ?? row.subtotal ?? row.total),
    }));

    const customersRate = (data.customersRate || []).slice(-21).map((row) => ({
        date: row.customer_date || row.date,
        total_customers: num(row.total_customers),
    }));

    const websiteVisitedRate = (data.WebsiteVisitedRate || data.websiteVisitedRate || [])
        .slice(-21)
        .map((row) => ({
            date: row.date,
            totalviews: num(row.totalviews ?? row.views),
        }));

    const checkoutVisitedRate = (data.checkout_VisitedRate || data.checkoutVisitedRate || [])
        .slice(-21)
        .map((row) => ({
            date: row.date,
            totalviews: num(row.totalviews ?? row.views),
        }));

    const peakTimes = (data.peakTimes || [])
        .filter((row) => row.hour != null)
        .map((row) => ({
            hour: num(row.hour),
            hour_label: formatPeakHour(row.hour),
            visit_count: num(row.visit_count),
        }))
        .sort((a, b) => b.visit_count - a.visit_count);

    const topOrderedItems = mapTopProductRows(data.topOrderedItems, {
        mode: 'sold',
        limit: 10,
    });

    const topVisitedProducts = mapTopProductRows(data.topVisitedProducts, {
        mode: 'views',
        limit: 10,
    });

    const topOrderedCities = (data.topOrderedCities || [])
        .filter((row) => row.name != null || row.city != null)
        .slice(0, 10)
        .map((row) => ({
            name: row.name || row.city,
            total_orders: num(row.total_orders ?? row.total_quantity),
        }));

    const topCustomers = (data.topCustomers || data.top_customers || [])
        .filter((row) => row.customername || row.name)
        .slice(0, 10)
        .map((row) => {
            const customerid = row.customerid || row.id || null;
            return {
                customerid: customerid ? String(customerid) : null,
                name: row.customername || row.name,
                total_orders: num(row.total_orders ?? row.total ?? row.orders_count),
                open_path: customerid
                    ? `/dashboard/institute/customer/showcustomer/${customerid}`
                    : null,
            };
        });

    const sumSeries = (rows, key) =>
        (rows || []).reduce((acc, row) => acc + num(row[key]), 0);

    return {
        ok: Boolean(data.status ?? true),
        source: 'getPagesAnalytics',
        focus,
        period: range.period,
        fromdate: range.fromdate,
        todate: range.todate,
        note:
            'Answer ANY store ranking/trend from these arrays only — names and counts are already here. ' +
            'Do NOT call get_product_info / fetchproductinfo for these lists. ' +
            'top_customers → best customers (total_orders). ' +
            'top_ordered_items → top selling products (total_quantity). ' +
            'top_visited_products → most viewed products (totalviews). ' +
            'top_ordered_cities → cities by orders. ' +
            'orders_rate → orders over time. subtotals_rate → order value over time (total_value). ' +
            'customers_rate → new customers over time. website_visited_rate / checkout_visited_rate → visits. ' +
            'peak_times → busiest visit hours (hour_label + visit_count). period=all = all-time.',
        totals: {
            orders_in_rate: sumSeries(ordersRate, 'total_orders'),
            order_value_in_rate: sumSeries(subtotalsRate, 'total_value'),
            new_customers_in_rate: sumSeries(customersRate, 'total_customers'),
            website_views_in_rate: sumSeries(websiteVisitedRate, 'totalviews'),
            checkout_views_in_rate: sumSeries(checkoutVisitedRate, 'totalviews'),
            top_cities_orders: sumSeries(topOrderedCities, 'total_orders'),
        },
        orders_rate: ordersRate,
        subtotals_rate: subtotalsRate,
        customers_rate: customersRate,
        website_visited_rate: websiteVisitedRate,
        checkout_visited_rate: checkoutVisitedRate,
        peak_times: peakTimes.slice(0, 12),
        top_ordered_items: topOrderedItems,
        top_visited_products: topVisitedProducts,
        top_ordered_cities: topOrderedCities,
        top_customers: topCustomers,
        top_customer: topCustomers[0] || null,
        // Back-compat aliases
        orders_rate_recent: ordersRate,
        subtotals_rate_recent: subtotalsRate,
        analytics_path: '/dashboard/institute/analytics',
    };
}

async function findCollections(args = {}) {
    const query = String(args.query || '').trim().toLowerCase();
    const limit = clampLimit(args.limit, 8);
    if (!query) {
        return { ok: false, error: 'query is required' };
    }

    const data = await postApi('/instowner/fetchcollection', { parentcollection_id: '' });
    const collections = data.collection || data.collections || data.data?.collection || [];

    const scored = collections
        .map((item) => {
            const title_en = String(item.title_en || '').trim();
            const title_ar = String(item.title_ar || '').trim();
            const hay = `${title_en} ${title_ar}`.toLowerCase();
            let score = 0;
            if (hay === query) score = 100;
            else if (title_en.toLowerCase() === query || title_ar.toLowerCase() === query) score = 95;
            else if (hay.startsWith(query)) score = 80;
            else if (hay.includes(query)) score = 60;
            else if (query.split(/\s+/).every((part) => hay.includes(part))) score = 40;
            return { item, score };
        })
        .filter((row) => row.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);

    const matches = scored.map(({ item, score }) => {
        const collectionid = item.collectionid || item.id;
        return {
            collectionid,
            title_en: item.title_en || '',
            title_ar: item.title_ar || '',
            score,
            open_path: `/dashboard/institute/collections/editcollection/${collectionid}?section=products`,
            open_info_path: `/dashboard/institute/collections/editcollection/${collectionid}?section=information`,
        };
    });

    return {
        ok: Boolean(data.status ?? true),
        query: args.query,
        count: matches.length,
        best_match: matches[0] || null,
        collections: matches,
    };
}

async function findProducts(args = {}) {
    const query = String(args.query || '').trim();
    const limit = clampLimit(args.limit, 8);
    if (!query) {
        return { ok: false, error: 'query is required' };
    }

    const extra = []
        .concat(args.alt_query || [], args.query_en || [], args.query_ar || [], args.alt_queries || [])
        .flatMap((v) => (Array.isArray(v) ? v : [v]))
        .map((v) => String(v || '').trim())
        .filter(Boolean);

    const variants = buildCustomerNameVariants(query, extra);
    const byId = new Map();
    const tried = [];

    for (const variant of variants) {
        if (toolAbortSignal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        tried.push(variant);
        try {
            const data = await postApi('/instowner/SearchProduct', { ProductSearchQuery: variant });
            const products = data.products || data.data?.products || [];
            const q = variant.toLowerCase();

            const scored = products
                .map((product) => {
                    const productid = product.productid || product.id;
                    const name = product.productname || product.name || product.title || product.name_en || '';
                    const name_en = product.name_en || product.productname || product.name || '';
                    const name_ar = product.name_ar || '';
                    const hay = `${name} ${name_en} ${name_ar}`.toLowerCase();
                    let score = 0;
                    if (name.toLowerCase() === q || name_en.toLowerCase() === q || name_ar.toLowerCase() === q) {
                        score = 100;
                    } else if (hay === q) score = 95;
                    else if (hay.startsWith(q) || name.toLowerCase().startsWith(q)) score = 80;
                    else if (new RegExp(`(?:^|\\s)${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s|$)`, 'i').test(hay)) {
                        score = 70;
                    } else if (hay.includes(q)) score = 50;
                    return {
                        score,
                        product: {
                            productid,
                            name,
                            name_en,
                            name_ar,
                            price: product.price || product.productprice,
                            open_path: productInfoPath(productid, 'information'),
                            matched_query: variant,
                        },
                    };
                })
                .filter((row) => row.product.productid);

            for (const row of scored) {
                const id = String(row.product.productid);
                const prev = byId.get(id);
                if (!prev || row.score > prev.score) byId.set(id, row);
            }
        } catch (err) {
            if (err?.name === 'AbortError') throw err;
            if (variants.length === 1) throw err;
        }
    }

    const matches = Array.from(byId.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((row) => row.product);

    return {
        ok: true,
        query,
        searched_variants: tried,
        count: matches.length,
        best_match: matches[0] || null,
        products: matches,
    };
}

async function resolveProductIdFromQuery(query, { forUpdate = false, alt_query, alt_queries, query_en, query_ar } = {}) {
    const found = await findProducts({ query, alt_query, alt_queries, query_en, query_ar, limit: 8 });
    if (!found.best_match?.productid) {
        return { ok: false, error: 'Product not found', query, searched_variants: found.searched_variants, products: [] };
    }

    const q = query.toLowerCase();
    const exact = found.products.filter(
        (p) =>
            String(p.name || '').toLowerCase() === q ||
            String(p.name_en || '').toLowerCase() === q ||
            String(p.name_ar || '').toLowerCase() === q,
    );

    if (exact.length === 1) {
        return { ok: true, productid: exact[0].productid, products: found.products };
    }
    if (found.count === 1) {
        return { ok: true, productid: found.best_match.productid, products: found.products };
    }
    if (exact.length > 1) {
        return {
            ok: false,
            needs_choice: true,
            stop_searching: true,
            error: 'Multiple products with that exact name. Ask which productid — do NOT search again.',
            query,
            products: exact,
        };
    }
    return {
        ok: false,
        needs_choice: true,
        stop_searching: true,
        error: forUpdate
            ? 'Multiple products matched. Show this list and ask which productid. Do NOT call find_products or update_product_info again until they choose.'
            : 'Multiple products matched. Ask which productid to use.',
        query,
        best_match: found.best_match,
        products: found.products,
    };
}

async function getProductInfo(args = {}) {
    let productid = String(args.productid || args.product_id || '').trim();
    const query = String(args.query || args.name || '').trim();
    let matches;

    if (!productid && query) {
        const resolved = await resolveProductIdFromQuery(query);
        matches = resolved.products;
        if (!resolved.ok) {
            return resolved;
        }
        productid = resolved.productid;
    }

    if (!productid) {
        return { ok: false, error: 'productid or query is required' };
    }

    const data = await postApi('/instowner/fetchproductinfo', { productid });
    const payload = data.data && typeof data.data === 'object' ? data.data : data;
    const info = payload.productinfo || payload.product || payload;

    return {
        ok: Boolean(payload.status ?? data.status ?? true),
        productid,
        matches,
        product: {
            productid: info.productid || productid,
            name_en: info.name_en || '',
            name_ar: info.name_ar || '',
            description_en: info.description_en || '',
            description_ar: info.description_ar || '',
            short_description_en: info.short_description_en || '',
            short_description_ar: info.short_description_ar || '',
            prodtranslationexist: info.prodtranslationexist,
            price: info.price || info.productprice,
            quantity: info.quantity || info.qty,
        },
        open_path: productInfoPath(productid, args.section || 'information'),
        auto_open: Boolean(args.section),
        navigate: args.section
            ? {
                  path: productInfoPath(productid, args.section),
                  label: info.name_en || info.name_ar || 'Product',
              }
            : undefined,
    };
}

/**
 * Update product info via fetchproductinfo + updateproductinfo.
 * Supports rename and EN/AR descriptions (full + short). Unspecified fields keep current values.
 * Always sends non-empty name_en/name_ar (API requires them). On 422, reads errors and retries once.
 */
function ensureProductNames(payload) {
    let name_en = String(payload.name_en || '').trim();
    let name_ar = String(payload.name_ar || '').trim();
    if (!name_en && name_ar) name_en = name_ar;
    if (!name_ar && name_en) name_ar = name_en;
    if (!name_en) name_en = 'Product';
    if (!name_ar) name_ar = name_en;
    return { ...payload, name_en, name_ar };
}

function fixPayloadFromValidationErrors(payload, errors = {}) {
    let next = { ...payload };
    for (const [field, msgs] of Object.entries(errors || {})) {
        const required = (msgs || []).some((m) => /required/i.test(String(m)));
        if (!required) continue;
        if (field === 'name_ar' && !String(next.name_ar || '').trim()) {
            next.name_ar = String(next.name_en || 'منتج').trim() || 'منتج';
            next.prodtranslationexist = 1;
        }
        if (field === 'name_en' && !String(next.name_en || '').trim()) {
            next.name_en = String(next.name_ar || 'Product').trim() || 'Product';
        }
        if ((field === 'description_en' || field === 'description_ar' || field === 'short_description_en' || field === 'short_description_ar') && (next[field] == null || next[field] === undefined)) {
            next[field] = '';
        }
    }
    return ensureProductNames(next);
}

async function postUpdateProductInfo(payload) {
    let body = ensureProductNames(payload);
    try {
        const data = await postApi('/instowner/updateproductinfo', body);
        return { data, payload: body, retried_validation: false };
    } catch (err) {
        if (err instanceof ApiError && err.status === 422 && err.data?.errors) {
            const fixed = fixPayloadFromValidationErrors(body, err.data.errors);
            const data = await postApi('/instowner/updateproductinfo', fixed);
            return {
                data,
                payload: fixed,
                retried_validation: true,
                fixed_fields: Object.keys(err.data.errors),
            };
        }
        throw err;
    }
}

async function fetchFullProductInfo(productid) {
    const data = await postApi('/instowner/fetchproductinfo', { productid });
    const payload = data.data && typeof data.data === 'object' ? data.data : data;
    return {
        ok: Boolean(payload.status ?? data.status ?? true),
        info: payload.productinfo || payload.product || payload,
        raw: payload,
    };
}

async function updateProductInfo(args = {}) {
    let productid = String(args.productid || args.product_id || '').trim();
    const query = String(args.query || args.current_name || args.name || '').trim();

    if (!productid && query) {
        const resolved = await resolveProductIdFromQuery(query, {
            forUpdate: true,
            alt_query: args.alt_query,
            alt_queries: args.alt_queries,
            query_en: args.query_en,
            query_ar: args.query_ar,
        });
        if (!resolved.ok) {
            return resolved;
        }
        productid = resolved.productid;
    }

    if (!productid) {
        return { ok: false, error: 'productid or query (product name) is required' };
    }

    const current = await getProductInfo({ productid });
    if (!current.ok || !current.product) {
        return { ok: false, error: current.error || 'Failed to load product', productid };
    }

    const info = current.product;
    const pick = (key, ...aliases) => {
        for (const a of aliases) {
            if (args[a] !== undefined && args[a] !== null) return String(args[a]);
        }
        if (args[key] !== undefined && args[key] !== null) return String(args[key]);
        return info[key] || '';
    };

    let name_en = pick('name_en', 'new_name');
    let name_ar = pick('name_ar');
    const description_en = pick('description_en', 'description');
    const description_ar = pick('description_ar');
    const short_description_en = pick('short_description_en', 'short_description');
    const short_description_ar = pick('short_description_ar');

    const touched = [
        'new_name',
        'name_en',
        'name_ar',
        'description',
        'description_en',
        'description_ar',
        'short_description',
        'short_description_en',
        'short_description_ar',
    ].some((k) => args[k] !== undefined && args[k] !== null);

    if (!touched) {
        return {
            ok: false,
            error: 'Provide at least one field to update: new_name/name_en/name_ar, description_en/description_ar, or short_description_en/short_description_ar',
            productid,
            open_path: productInfoPath(productid, 'information'),
        };
    }

    const arFieldsTouched = ['name_ar', 'description_ar', 'short_description_ar'].some(
        (k) => args[k] !== undefined && args[k] !== null && String(args[k]).trim() !== '',
    );
    const prodtranslationexist = arFieldsTouched || !String(name_ar || info.name_ar || '').trim() ? 1 : info.prodtranslationexist ?? 0;

    const payload = ensureProductNames({
        functype: 'Update Information',
        productid,
        prodtranslationexist,
        name_en,
        name_ar,
        description_en,
        description_ar,
        short_description_en,
        short_description_ar,
    });
    name_en = payload.name_en;
    name_ar = payload.name_ar;

    const { data, retried_validation, fixed_fields } = await postUpdateProductInfo(payload);
    const ok = Boolean(data.status ?? data.data?.status ?? true);

    const changes = [];
    if (name_en !== (info.name_en || '')) changes.push(`name_en → "${name_en}"`);
    if (name_ar !== (info.name_ar || '')) changes.push(`name_ar → "${name_ar}"`);
    if (description_en !== (info.description_en || '')) changes.push('description_en updated');
    if (description_ar !== (info.description_ar || '')) changes.push('description_ar updated');
    if (short_description_en !== (info.short_description_en || '')) changes.push('short_description_en updated');
    if (short_description_ar !== (info.short_description_ar || '')) changes.push('short_description_ar updated');

    const fullDescTouched = ['description', 'description_en', 'description_ar'].some(
        (k) => args[k] !== undefined && args[k] !== null,
    );
    const shortTouched = ['short_description', 'short_description_en', 'short_description_ar'].some(
        (k) => args[k] !== undefined && args[k] !== null,
    );

    return {
        ok,
        updated: ok,
        productid,
        prodtranslationexist,
        retried_validation: Boolean(retried_validation),
        fixed_fields: fixed_fields || undefined,
        before: {
            name_en: info.name_en,
            name_ar: info.name_ar,
            description_en: info.description_en,
            description_ar: info.description_ar,
            short_description_en: info.short_description_en,
            short_description_ar: info.short_description_ar,
            prodtranslationexist: info.prodtranslationexist,
        },
        after: {
            name_en,
            name_ar,
            description_en,
            description_ar,
            short_description_en,
            short_description_ar,
            prodtranslationexist,
        },
        ask_short_description: false,
        open_path: productInfoPath(productid, 'information'),
        auto_open: true,
        navigate: { path: productInfoPath(productid, 'information'), label: name_en || name_ar || 'Product' },
        message: ok
            ? changes.length
                ? `Updated product: ${changes.join('; ')}`
                : 'Product saved (no field differences detected)'
            : data.message || data.data?.message || 'Update failed',
        hint: undefined,
    };
}

const BULK_MAX = 10;
function clampBulk(value, fallback = BULK_MAX) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return Math.min(Math.floor(n), BULK_MAX);
}

function summarizeBulkProduct(product) {
    const productid = product.productid || product.id;
    const name =
        product.name_en ||
        product.productname ||
        product.name ||
        product.title ||
        product?.productinfo?.name_en ||
        '';
    const name_ar = product.name_ar || product?.productinfo?.name_ar || '';
    return {
        productid,
        name,
        name_en: name,
        name_ar,
        price: product.price || product.productprice || product.defaultprice,
        saleprice: product.saleprice || product.defaultsaleprice,
        hassale: product.hassale,
        open_path: productInfoPath(productid, 'information'),
    };
}

/**
 * List up to 10 products: latest store products OR products inside a named collection.
 */
async function listBulkProducts(args = {}) {
    const limit = clampBulk(args.limit, BULK_MAX);
    const collectionQuery = String(args.collection || args.collection_query || args.collection_name || '').trim();
    const scope = String(args.scope || (collectionQuery ? 'collection' : 'latest')).toLowerCase();
    const offset = Math.max(0, Math.floor(Number(args.offset) || 0));

    if (scope === 'collection' || collectionQuery) {
        if (!collectionQuery) {
            return { ok: false, error: 'collection name is required for scope=collection' };
        }
        const found = await findCollections({ query: collectionQuery, limit: 5 });
        if (!found.best_match?.collectionid) {
            return { ok: false, error: 'Collection not found', query: collectionQuery, collections: found.collections || [] };
        }
        if ((found.collections || []).length > 1 && found.best_match.score < 95) {
            return {
                ok: false,
                needs_choice: true,
                error: 'Multiple collections matched. Ask which collectionid.',
                collections: found.collections,
            };
        }

        const collectionid = found.best_match.collectionid;
        const members = await collectCollectionMemberProductIds(collectionid);
        const allIds = [...members.ids];
        if (!allIds.length) {
            return {
                ok: false,
                error: `Collection "${found.best_match.title_en || collectionQuery}" has no products found via fetchcollectioninfo/fetchproducts.`,
                collection: found.best_match,
                member_sources: members.sources,
            };
        }
        // Prefer full product objects from fetchproducts when available; otherwise summarize ids.
        let all = [];
        try {
            const data = await postApi('/instowner/fetchproducts', {
                filter: 0,
                collectionid: String(collectionid),
                vendorid: 'all',
                hassale: 'all',
                productactive: 'all',
                page: 0,
                outofstock: 'all',
                productindex_sortby: '',
                hasvariants: 'all',
                features: [],
            });
            all = data.products || data.data?.products || [];
        } catch (_) {
            all = [];
        }
        if (!all.length && Array.isArray(members.collectioninfo?.products)) {
            all = members.collectioninfo.products;
        }
        if (!all.length) {
            all = allIds.map((productid) => ({ productid }));
        }
        const slice = all.slice(offset, offset + limit).map(summarizeBulkProduct);

        return {
            ok: true,
            scope: 'collection',
            collection: found.best_match,
            total: Math.max(all.length, allIds.length),
            offset,
            count: slice.length,
            has_more: offset + slice.length < Math.max(all.length, allIds.length),
            remaining: Math.max(0, Math.max(all.length, allIds.length) - (offset + slice.length)),
            products: slice,
            member_sources: members.sources,
            open_path: found.best_match.open_path,
            note: `Hard max ${BULK_MAX} products per call. If has_more, ask to continue with offset=${offset + slice.length}.`,
        };
    }

    const data = await postApi('/instowner/fetchproducts', {
        filter: 0,
        collectionid: 'all',
        vendorid: 'all',
        hassale: 'all',
        productactive: 'all',
        page: 0,
        outofstock: 'all',
        productindex_sortby: '',
        hasvariants: 'all',
        features: [],
    });
    const all = data.products || data.data?.products || [];
    const slice = all.slice(offset, offset + limit).map(summarizeBulkProduct);
    const total = data.allproductscount ?? data.productscount ?? all.length;

    return {
        ok: Boolean(data.status ?? true),
        scope: 'latest',
        total: Number(total) || all.length,
        offset,
        count: slice.length,
        has_more: offset + slice.length < all.length,
        remaining: Math.max(0, all.length - (offset + slice.length)),
        products: slice,
        note: `Hard max ${BULK_MAX} products per call (latest/first page). If has_more, continue with offset.`,
    };
}

/**
 * Apply updates to up to 10 products (descriptions/names). Model writes the copy.
 */
async function bulkUpdateProducts(args = {}) {
    const items = Array.isArray(args.items) ? args.items : Array.isArray(args.products) ? args.products : [];
    if (!items.length) {
        return { ok: false, error: 'items[] is required (max 10 product updates)' };
    }
    if (items.length > BULK_MAX) {
        return {
            ok: false,
            error: `Max ${BULK_MAX} products per call. Split into batches.`,
            received: items.length,
        };
    }

    const results = [];
    for (const item of items) {
        if (toolAbortSignal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        try {
            const updated = await updateProductInfo(item || {});
            results.push({
                productid: updated.productid || item.productid,
                ok: Boolean(updated.ok),
                message: updated.message || updated.error,
                open_path: updated.open_path,
                retried_validation: updated.retried_validation,
            });
        } catch (err) {
            results.push({
                productid: item.productid,
                ok: false,
                error: err.message || 'Update failed',
                validation_errors: err.data?.errors,
            });
        }
    }

    const okCount = results.filter((r) => r.ok).length;
    return {
        ok: okCount > 0,
        updated_count: okCount,
        failed_count: results.length - okCount,
        results,
        note: `Processed ${results.length}/${BULK_MAX} max. Summarize successes/failures to the user.`,
    };
}

function readProductBasePrice(info) {
    const currencies = Array.isArray(info.productcurrencies) ? info.productcurrencies : [];
    const fromCurrency = currencies.find((c) => c?.productprice?.price != null && String(c.productprice.price).trim() !== '');
    if (fromCurrency) return num(fromCurrency.productprice.price);
    if (info.defaultprice != null && !Array.isArray(info.defaultprice) && String(info.defaultprice).trim() !== '') {
        return num(info.defaultprice);
    }
    if (Array.isArray(info.defaultprice) && info.defaultprice.length) {
        return num(info.defaultprice[0]?.price ?? info.defaultprice[0]);
    }
    return num(info.price || info.productprice);
}

/**
 * Correct API for price: functype "Update Default Pricing" (NOT "Update Information").
 */
async function saveProductPricing(info, { saleprice, hassale, price } = {}) {
    const productid = info.productid;
    const preserveHassale = hassale == null ? Number(info.hassale) === 1 : Boolean(hassale);

    const currencies = Array.isArray(info.productcurrencies)
        ? info.productcurrencies.map((c) => {
              const productprice = { ...(c.productprice || {}) };
              if (price != null && Number.isFinite(Number(price))) {
                  productprice.price = String(price);
              }
              if (saleprice != null && Number.isFinite(Number(saleprice))) {
                  productprice.saleprice = String(saleprice);
              }
              if (hassale != null) {
                  productprice.hassale = hassale ? 1 : 0;
              }
              return { ...c, productprice };
          })
        : [];

    let defaultprice = info.defaultprice;
    if (price != null && Number.isFinite(Number(price))) {
        if (Array.isArray(defaultprice) && defaultprice.length && typeof defaultprice[0] === 'object') {
            defaultprice = defaultprice.map((row) => ({ ...row, price: String(price) }));
        } else {
            defaultprice = String(price);
        }
    }
    if (!defaultprice || (Array.isArray(defaultprice) && defaultprice.length === 0) || String(defaultprice).trim() === '') {
        const base = price != null ? price : readProductBasePrice(info);
        defaultprice = String(base || '0');
    }

    let defaultsaleprice = info.defaultsaleprice;
    if (saleprice != null && Number.isFinite(Number(saleprice))) {
        defaultsaleprice = String(saleprice);
    }

    const payload = {
        functype: 'Update Default Pricing',
        productid,
        defaultprice,
        defaultsaleprice: defaultsaleprice ?? '',
        hassale: preserveHassale ? 1 : 0,
        productcurrencies: currencies,
        overwritevariants: 0,
    };

    const data = await postApi('/instowner/updateproductinfo', payload);
    const ok = Boolean(data.status ?? data.data?.status ?? true);
    return { ok, data, payload };
}

/**
 * Change product price (and optional sale price). Uses Update Default Pricing only.
 */
async function updateProductPrice(args = {}) {
    const priceRaw = args.price ?? args.new_price ?? args.amount;
    const price = priceRaw === undefined || priceRaw === null || priceRaw === '' ? null : Number(priceRaw);
    const saleRaw = args.saleprice ?? args.sale_price;
    const saleprice = saleRaw === undefined || saleRaw === null || saleRaw === '' ? null : Number(saleRaw);
    const clearSale = Boolean(args.clear_sale || args.remove_sale);

    if ((price == null || !Number.isFinite(price) || price < 0) && (saleprice == null || !Number.isFinite(saleprice)) && !clearSale) {
        return { ok: false, error: 'Provide price and/or saleprice (numbers). Example: price=500' };
    }

    let productid = String(args.productid || args.product_id || '').trim();
    const query = String(args.query || args.name || '').trim();
    if (!productid && query) {
        const resolved = await resolveProductIdFromQuery(query, {
            forUpdate: true,
            alt_query: args.alt_query,
            alt_queries: args.alt_queries,
            query_en: args.query_en,
            query_ar: args.query_ar,
        });
        if (!resolved.ok) return resolved;
        productid = resolved.productid;
    }
    if (!productid) {
        return { ok: false, error: 'productid or query is required' };
    }

    const full = await fetchFullProductInfo(productid);
    if (!full.ok || !full.info) {
        return { ok: false, error: 'Failed to load product', productid };
    }

    const before = readProductBasePrice(full.info);
    const saved = await saveProductPricing(full.info, {
        price: price != null && Number.isFinite(price) ? price : undefined,
        saleprice: clearSale ? 0 : saleprice != null && Number.isFinite(saleprice) ? saleprice : undefined,
        hassale: clearSale ? false : saleprice != null ? true : undefined,
    });

    const open_path = productInfoPath(productid, 'stockandpricing');
    return {
        ok: saved.ok,
        updated: saved.ok,
        productid,
        before_price: before,
        after_price: price != null && Number.isFinite(price) ? price : before,
        saleprice: clearSale ? null : saleprice,
        functype_used: 'Update Default Pricing',
        open_path,
        auto_open: true,
        navigate: { path: open_path, label: full.info.name_en || full.info.name_ar || 'Product' },
        message: saved.ok
            ? `Price updated via Update Default Pricing${price != null ? ` → ${price}` : ''}${saleprice != null ? ` (sale ${saleprice})` : ''}`
            : saved.data?.message || saved.data?.data?.message || 'Price update failed',
        note: 'Do NOT use update_product_info / Update Information for prices.',
    };
}

/**
 * Change stock quantity via functype "Update Product Quantity".
 */
async function updateProductQuantity(args = {}) {
    const quantity = Number(args.quantity ?? args.productquantity ?? args.qty);
    if (!Number.isFinite(quantity) || quantity < 0) {
        return { ok: false, error: 'quantity is required (non-negative number)' };
    }
    const mode = String(args.mode || args.updatequantitytypeinput || 'set').toLowerCase();
    const updatequantitytypeinput = ['set', 'add', 'setandclear'].includes(mode) ? mode : 'set';

    let productid = String(args.productid || args.product_id || '').trim();
    const query = String(args.query || args.name || '').trim();
    if (!productid && query) {
        const resolved = await resolveProductIdFromQuery(query, {
            forUpdate: true,
            alt_query: args.alt_query,
            alt_queries: args.alt_queries,
        });
        if (!resolved.ok) return resolved;
        productid = resolved.productid;
    }
    if (!productid) {
        return { ok: false, error: 'productid or query is required' };
    }

    const payload = {
        functype: 'Update Product Quantity',
        ComponentFetchingType: 'product',
        productid,
        productquantity: String(Math.floor(quantity)),
        updatequantitytypeinput,
    };

    const data = await postApi('/instowner/updateproductinfo', payload);
    const ok = Boolean(data.status ?? data.data?.status ?? true);
    const open_path = productInfoPath(productid, 'stockandpricing');

    return {
        ok,
        updated: ok,
        productid,
        quantity: Math.floor(quantity),
        mode: updatequantitytypeinput,
        functype_used: 'Update Product Quantity',
        open_path,
        auto_open: true,
        navigate: { path: open_path, label: 'Product inventory' },
        message: ok
            ? `Quantity ${updatequantitytypeinput} to ${Math.floor(quantity)}`
            : data.message || data.data?.message || 'Quantity update failed',
        note: 'Do NOT use update_product_info for quantity.',
    };
}

/**
 * Collect productids belonging to a collection from every reliable source:
 * - collectioninfo.products (manual picker join)
 * - fetchproducts filtered by collectionid (storefront/catalog membership)
 * Product import with refcollectionid often fills the latter without the former.
 */
function extractProductIdsFromList(list) {
    if (list == null) return [];
    if (typeof list === 'string' || typeof list === 'number') {
        return String(list)
            .split(/[,|;]+/)
            .map((s) => s.trim())
            .filter(Boolean);
    }
    if (!Array.isArray(list)) return [];
    return list
        .map((p) => {
            if (p == null) return '';
            if (typeof p === 'string' || typeof p === 'number') return String(p).trim();
            return String(
                p.productid || p.id || p.ProductId || p.refproductid || p.product_id || '',
            ).trim();
        })
        .filter(Boolean);
}

async function collectCollectionMemberProductIds(collectionid) {
    const id = String(collectionid || '').trim();
    const ids = new Set();
    const sources = [];
    let collectioninfo = null;

    if (!id) return { ids, sources, collectioninfo };

    try {
        const infoData = await postApi('/instowner/fetchcollectioninfo', { collectionid: id });
        const info =
            infoData.data?.collectioninfo ||
            infoData.collectioninfo ||
            infoData.data ||
            {};
        collectioninfo = info;
        const fromInfo = [
            ...extractProductIdsFromList(info.products),
            ...extractProductIdsFromList(info.collectionproducts),
            ...extractProductIdsFromList(info.productids),
            ...extractProductIdsFromList(info.productsids),
        ];
        fromInfo.forEach((pid) => ids.add(pid));
        if (fromInfo.length) sources.push('fetchcollectioninfo');
    } catch (_) {
        /* continue to fetchproducts */
    }

    try {
        let page = 0;
        let sawPage = false;
        // Paginate until a page returns no products (hard cap).
        while (page < 80) {
            if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
            const data = await postApi('/instowner/fetchproducts', {
                filter: 0,
                collectionid: id,
                vendorid: 'all',
                hassale: 'all',
                productactive: 'all',
                page,
                outofstock: 'all',
                productindex_sortby: '',
                hasvariants: 'all',
                features: [],
            });
            const products = data.products || data.data?.products || [];
            if (!Array.isArray(products) || !products.length) break;
            sawPage = true;
            for (const p of products) {
                const pid = String(p.productid || p.id || '').trim();
                if (pid) ids.add(pid);
            }
            const total = Number(data.allproductscount ?? data.productscount ?? 0);
            if (total > 0 && ids.size >= total) break;
            // If API ignores page and always returns the same set, stop after first.
            if (page > 0 && products.length && total <= products.length) break;
            page += 1;
        }
        if (sawPage) sources.push('fetchproducts');
    } catch (_) {
        /* export-sheet matching remains as fallback */
    }

    return { ids, sources, collectioninfo };
}

/**
 * Apply % discount via products export sheet → edit sale fields → re-import.
 * Collection scope: resolve collectionid via fetchcollections, load member productids
 * from fetchcollectioninfo + fetchproducts, then match those ids (and refcollectionid)
 * in the full products export sheet.
 */
async function applyProductDiscount(args = {}) {
    const percent = Number(args.percent ?? args.discount_percent ?? args.discount);
    if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) {
        return {
            ok: false,
            error: 'percent is required (e.g. 10 for 10% off). Must be between 0 and 100 exclusive.',
        };
    }

    const collectionQuery = String(
        args.collection || args.collection_query || args.collection_name || '',
    ).trim();
    const scope = String(args.scope || (collectionQuery ? 'collection' : 'all')).trim().toLowerCase();

    let collection = null;
    let collectionid = null;
    /** @type {Set<string>|null} */
    let memberProductIds = null;
    let memberSources = [];

    if (scope === 'collection' || collectionQuery) {
        if (!collectionQuery) {
            return { ok: false, error: 'collection name is required for a collection discount' };
        }
        const found = await findCollectionExactOrBest(collectionQuery, 12);
        if (!found.best_match?.collectionid) {
            return {
                ok: false,
                error: `Collection not found: "${collectionQuery}"`,
                collections: found.collections || [],
            };
        }
        if ((found.collections || []).length > 1 && found.best_match.score < 95 && !found.exact) {
            return {
                ok: false,
                needs_choice: true,
                error: 'Multiple collections matched. Ask which collectionid.',
                collections: found.collections,
                best_match: found.best_match,
            };
        }
        collection = found.best_match;
        collectionid = String(found.best_match.collectionid);

        const members = await collectCollectionMemberProductIds(collectionid);
        memberProductIds = members.ids;
        memberSources = members.sources;
        // Do NOT abort when empty — products imported with refcollectionid often
        // won't appear in collectioninfo.products; export-sheet matching still works.
    }

    const currency = await fetchDefaultCurrency();
    const currencyid =
        currency.currencyid || currency.defaultcurrency || currency.id || 'defaultcurrency';

    // Always export the full catalog sheet, then select rows by productid / refcollectionid.
    const filter = {
        filter: 0,
        collectionid: 'all',
        vendorid: 'all',
        hassale: 'all',
        productactive: 'all',
        page: 0,
        outofstock: 'all',
        productindex_sortby: '',
        hasvariants: 'all',
        features: [],
        currencyid,
    };

    if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const exportUrl =
        `${serverbaselink}/instowner/exportproducts?filter=` +
        encodeURIComponent(JSON.stringify(filter));
    const exportRes = await fetch(exportUrl, {
        method: 'GET',
        credentials: 'include',
        signal: toolAbortSignal || undefined,
    });
    if (!exportRes.ok) {
        return {
            ok: false,
            error: `exportproducts failed (HTTP ${exportRes.status})`,
            filter,
        };
    }

    const exportBuf = await exportRes.arrayBuffer();
    if (!exportBuf || exportBuf.byteLength < 32) {
        return { ok: false, error: 'exportproducts returned an empty file' };
    }

    const workbook = XLSX.read(exportBuf, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        return { ok: false, error: 'exportproducts sheet is empty' };
    }
    const sheet = workbook.Sheets[sheetName];
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
    if (!aoa.length) {
        return { ok: false, error: 'exportproducts has no rows' };
    }

    const headers = (aoa[0] || []).map((h) => String(h || '').trim());
    const normHeader = (h) => String(h || '').trim().toLowerCase().replace(/[\s_]+/g, '');
    const col = (...names) => {
        const wanted = names.map(normHeader);
        return headers.findIndex((h) => wanted.includes(normHeader(h)));
    };

    const priceCol = col('price');
    const saleCol = col('sale_price', 'saleprice');
    const hasSaleCol = col('has_sale', 'hassale');
    const refCol = col('refcollectionid', 'collectionid');
    const collNameCol = col('collectionname', 'collection_name');
    const nameCol = col('name_en', 'name');
    const skuCol = col('sku');
    const idCol = col('productid', 'id');

    if (priceCol < 0 || saleCol < 0 || hasSaleCol < 0) {
        return {
            ok: false,
            error: 'Export sheet missing price / sale_price / has_sale columns',
            headers,
        };
    }

    const titleEn = String(collection?.title_en || '').trim().toLowerCase();
    const titleAr = String(collection?.title_ar || '').trim().toLowerCase();
    const qNorm = collectionQuery.toLowerCase();

    // Seed member set from export when API membership was empty
    if (collectionid && memberProductIds && !memberProductIds.size) {
        for (let r = 1; r < aoa.length; r += 1) {
            const row = aoa[r];
            if (!Array.isArray(row)) continue;
            let hit = false;
            if (refCol >= 0) {
                const refs = String(row[refCol] ?? '')
                    .split(/[,|;]+/)
                    .map((s) => s.trim())
                    .filter(Boolean);
                if (refs.some((ref) => ref === String(collectionid))) hit = true;
            }
            if (!hit && collNameCol >= 0) {
                const cname = String(row[collNameCol] ?? '').trim().toLowerCase();
                if (
                    cname &&
                    (cname === qNorm ||
                        cname === titleEn ||
                        cname === titleAr ||
                        (titleEn && (cname.includes(titleEn) || titleEn.includes(cname))) ||
                        (qNorm && (cname.includes(qNorm) || qNorm.includes(cname))))
                ) {
                    hit = true;
                }
            }
            if (!hit) continue;
            const pid = idCol >= 0 ? String(row[idCol] ?? '').trim() : '';
            if (pid) memberProductIds.add(pid);
        }
        if (memberProductIds.size) memberSources.push('export_sheet');
    }

    const factor = 1 - percent / 100;
    let updated = 0;
    let skipped = 0;
    const preview = [];
    const matchedVia = { productid: 0, refcollectionid: 0, collectionname: 0 };

    const rowMatchesCollection = (row) => {
        if (!collectionid) return true;

        const pid = idCol >= 0 ? String(row[idCol] ?? '').trim() : '';
        if (pid && memberProductIds?.has(pid)) {
            matchedVia.productid += 1;
            return true;
        }

        // refcollectionid may be a single id or comma-separated list
        if (refCol >= 0) {
            const refs = String(row[refCol] ?? '')
                .split(/[,|;]+/)
                .map((s) => s.trim())
                .filter(Boolean);
            if (refs.some((r) => r === String(collectionid))) {
                matchedVia.refcollectionid += 1;
                return true;
            }
        }

        if (collNameCol >= 0) {
            const cname = String(row[collNameCol] ?? '').trim().toLowerCase();
            if (
                cname &&
                (cname === qNorm ||
                    cname === titleEn ||
                    cname === titleAr ||
                    (titleEn && cname.includes(titleEn)) ||
                    (qNorm && cname.includes(qNorm)))
            ) {
                matchedVia.collectionname += 1;
                return true;
            }
        }

        return false;
    };

    for (let r = 1; r < aoa.length; r += 1) {
        const row = aoa[r];
        if (!Array.isArray(row) || row.every((c) => c === '' || c == null)) continue;

        if (collectionid && !rowMatchesCollection(row)) {
            skipped += 1;
            continue;
        }

        const base = Number(String(row[priceCol]).replace(/,/g, ''));
        if (!Number.isFinite(base) || base <= 0) {
            skipped += 1;
            continue;
        }

        // Stacking: if already on sale, discount from current sale_price; else from price
        const existingSale = Number(String(row[saleCol] ?? '').replace(/,/g, ''));
        const hasSaleNow = String(row[hasSaleCol] ?? '').trim() === '1';
        const baseForDiscount =
            hasSaleNow && Number.isFinite(existingSale) && existingSale > 0 ? existingSale : base;

        const sale = Math.round(baseForDiscount * factor * 100) / 100;
        row[saleCol] = sale;
        row[hasSaleCol] = '1';
        while (row.length < headers.length) row.push('');
        updated += 1;

        if (preview.length < 8) {
            preview.push({
                productid: idCol >= 0 ? String(row[idCol] || '') : '',
                sku: skuCol >= 0 ? String(row[skuCol] || '') : '',
                name: nameCol >= 0 ? String(row[nameCol] || '') : '',
                price: base,
                previous_sale: hasSaleNow ? existingSale : null,
                sale_price: sale,
                discount_percent: percent,
                refcollectionid: refCol >= 0 ? String(row[refCol] || '') : '',
            });
        }
    }

    if (!updated) {
        return {
            ok: false,
            error: collectionid
                ? `No export rows matched collection "${collection?.title_en || collectionQuery}" (id=${collectionid}). Collection has ${memberProductIds?.size || 0} products — check productid column in export.`
                : 'No products with a valid price found in the export sheet.',
            exported_rows: Math.max(0, aoa.length - 1),
            skipped,
            collectionid,
            collection_product_count: memberProductIds?.size || 0,
            member_sources: memberSources,
            sample_collection_productids: memberProductIds
                ? [...memberProductIds].slice(0, 8)
                : [],
            headers,
        };
    }

    const outSheet = XLSX.utils.aoa_to_sheet(aoa);
    const outBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(outBook, outSheet, sheetName || 'Products');
    const filename = `discount-${percent}pct-${
        collectionid ? catalogRowId(collection?.title_en || collectionQuery || 'collection', 0) : 'all'
    }.xlsx`;
    const xlsxArray = XLSX.write(outBook, { bookType: 'xlsx', type: 'array' });
    const file = new File([xlsxArray], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const alsoDownload = args.also_download !== false;
    if (alsoDownload) {
        XLSX.writeFile(outBook, filename);
    }

    const form = new FormData();
    form.append('productfile', file);
    form.append('currencyid', String(currencyid));
    form.append('vendorid', String(args.vendorid || ''));
    form.append('quantitytype', String(args.quantitytype || 'setandclear'));
    form.append('updatevariantsprice', String(args.updatevariantsprice || 'no'));

    if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const importRes = await fetch(serverbaselink + '/instowner/importproductsfile', {
        method: 'POST',
        credentials: 'include',
        body: form,
        signal: toolAbortSignal || undefined,
    });
    const importData = await importRes.json().catch(() => ({}));
    const importedOk = importRes.ok && Boolean(importData.status);

    const openPath = collectionid
        ? `/dashboard/institute/collections/editcollection/${collectionid}?section=products`
        : '/dashboard/institute/products/all/0';
    const label = collection?.title_en || collection?.title_ar || collectionQuery || 'Products';

    return {
        ok: importedOk,
        method: 'export_edit_import',
        discount_percent: percent,
        scope: collectionid ? 'collection' : 'all',
        collection: collection
            ? {
                  collectionid,
                  title_en: collection.title_en,
                  title_ar: collection.title_ar,
              }
            : null,
        collectionid,
        collection_product_count: memberProductIds?.size || null,
        member_sources: collectionid ? memberSources : null,
        matched_via: collectionid ? matchedVia : null,
        updated_count: updated,
        skipped_count: skipped,
        exported_rows: Math.max(0, aoa.length - 1),
        filename,
        downloaded: alsoDownload,
        uploaded: importedOk,
        preview,
        open_path: openPath,
        auto_open: importedOk,
        navigate: importedOk ? { path: openPath, label } : null,
        error: importedOk
            ? undefined
            : importData.reason || importData.message || `importproductsfile HTTP ${importRes.status}`,
        ...slimApi(
            {
                percent,
                collectionid,
                member_count: memberProductIds?.size || null,
                member_sources: memberSources,
                updated,
            },
            importData,
        ),
        note: importedOk
            ? `Exported all products, matched ${updated} row(s)${
                  collectionid
                      ? ` to collection ${label} (${collectionid}) via ${
                            memberSources.length ? memberSources.join('+') : 'export sheet'
                        }`
                      : ''
              }, applied ${percent}% sale, and re-imported.`
            : `Sheet updated (${updated} rows) but import failed — see error.`,
    };
}

/** Columns from Tabex Products export / re-import sheet (exportproducts). */
const PRODUCT_IMPORT_HEADERS = [
    'productid',
    'sku',
    'name_en',
    'name_ar',
    'name_chin',
    'description_en',
    'description_ar',
    'description_chin',
    'short_description_en',
    'short_description_ar',
    'productavail',
    'quantity',
    'price',
    'sale_price',
    'has_sale',
    'measurement_unit',
    'minimumproductquant',
    'maximumproductquant',
    'maximumproductquantityperday',
    'orderedquantity',
    'productcashbackvalue',
    'productindex',
    'deliverydatetext',
    'quantavailtype',
    'productshippingfees',
    'cost_price',
    'vendorname',
    'vendorphone',
    'vendorproviderid',
    'productcanrate',
    'isrequiredsubscription',
    'is_unify_variants_quantity',
    'prodaffpercentprofit',
    'lastquantityadded',
    'tags',
    'refcollectionid',
    'collectionname',
    'MainImageLink',
    'images_links',
    'productsitelink',
];

const PRODUCT_IMPORT_MAX_ROWS = 50;
const USER_FILE_PRODUCT_MAX_ROWS = 2000;

async function findCollectionExactOrBest(collectionQuery, limit = 8) {
    const found = await findCollections({ query: collectionQuery, limit });
    if (!found.ok || !found.best_match?.collectionid) return found;
    const q = String(collectionQuery || '').trim().toLowerCase();
    const qStem = q.replace(/s\b/, '');
    const exact = (found.collections || []).find((c) => {
        const en = String(c.title_en || '').toLowerCase().trim();
        const ar = String(c.title_ar || '').toLowerCase().trim();
        return (
            en === q ||
            ar === q ||
            en === `${q}s` ||
            q === `${en}s` ||
            (qStem && (en === qStem || en === `${qStem}s`)) ||
            (en && q && (en.includes(q) || q.includes(en)))
        );
    });
    if (exact) {
        return { ...found, best_match: exact, exact: true };
    }
    return found;
}

/**
 * Create a single collection via import sheet (never collectioncrud / never FE image search).
 */
async function createCollectionViaImport(titleEn, titleAr, descriptionEn, descriptionAr, imageQuery, imagesLinks) {
    const name = String(titleEn || '').trim();
    if (!name) return { ok: false, error: 'title required' };
    const query = imageQuery || `${name} fashion collection photography`;
    let imageUrl = String(imagesLinks || '').trim();
    if (!imageUrl) {
        imageUrl = await fetchAiImageUrl(query);
    }
    const result = await importCatalogGroups({
        grouptype: 'collection',
        with_images: false,
        also_download: false,
        filename: `collection-${name.replace(/\s+/g, '-').toLowerCase()}.xlsx`,
        items: [
            {
                // Distinct sheet id so multi-row imports never collapse into one collection
                id: catalogRowId(name, 0),
                name_en: name,
                name_ar: String(titleAr || name).trim(),
                description_en:
                    descriptionEn ||
                    `Shop our ${name} collection — curated styles with quality materials.`,
                description_ar:
                    descriptionAr ||
                    `تسوق مجموعة ${name} — تصاميم مختارة بخامات عالية الجودة.`,
                showtocustomers: 1,
                showinfilter: 1,
                skiplayeronclick: 0,
                parentindex: 0,
                image_query: query,
                images_links: imageUrl,
            },
        ],
    });
    return result;
}

/**
 * Ensure a collection exists before product import.
 * Uses import_catalog_groups only (never collectioncrud / FE Pexels).
 */
async function resolveCollectionForImport(args = {}) {
    let collectionid = String(args.collectionid || args.refcollectionid || '').trim();
    const collectionQuery = String(
        args.collection_query || args.collection || args.collectionname || '',
    ).trim();
    let collection = null;
    let created_collection = null;
    const attempts = [];

    const createIfMissing = collectionQuery
        ? args.create_collection_if_missing !== false
        : Boolean(args.create_collection_if_missing);
    const requireCollection = Boolean(
        args.require_collection === true ||
            (collectionQuery && args.require_collection !== false) ||
            collectionid,
    );

    const findExact = async (query, label) => {
        const found = await findCollectionExactOrBest(query, 12);
        attempts.push({
            step: label,
            count: found.count,
            best: found.best_match?.collectionid || null,
            exact: Boolean(found.exact),
        });
        return found;
    };

    const waitFind = async (query, labelPrefix, tries = 5, delayMs = 900) => {
        let found = { ok: false };
        for (let i = 0; i < tries; i += 1) {
            if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
            if (i > 0) await sleep(delayMs);
            found = await findExact(query, `${labelPrefix}_${i + 1}`);
            if (found.ok && found.best_match?.collectionid) {
                // Prefer exact title match so Bags ≠ Fashion Bags collision
                if (found.exact || found.best_match.score >= 95 || (found.collections || []).length === 1) {
                    return found;
                }
            }
        }
        return found;
    };

    if (!collectionid && collectionQuery) {
        let found = await waitFind(collectionQuery, 'find', 2, 400);

        if (found.ok && found.best_match?.collectionid) {
            if ((found.collections || []).length > 1 && found.best_match.score < 95 && !found.exact) {
                return {
                    ok: false,
                    needs_choice: true,
                    error: 'Multiple collections matched. Ask which collectionid.',
                    query: collectionQuery,
                    collections: found.collections,
                    best_match: found.best_match,
                };
            }
            collectionid = String(found.best_match.collectionid);
            collection = found.best_match;
        } else if (createIfMissing) {
            try {
                const imported = await createCollectionViaImport(collectionQuery);
                attempts.push({
                    step: 'import_catalog_groups',
                    ok: imported.ok,
                    error: imported.error || imported.reason,
                    images_attached: imported.images_attached,
                });
                found = await waitFind(collectionQuery, 'find_after_import', 6, 900);
                if (found.best_match?.collectionid) {
                    collectionid = String(found.best_match.collectionid);
                    collection = found.best_match;
                    created_collection = {
                        ok: true,
                        via: 'import_catalog_groups',
                        collectionid,
                        import_result: imported,
                    };
                }
            } catch (err) {
                attempts.push({ step: 'import_catalog_groups', ok: false, error: err.message });
            }

            if (!collectionid) {
                created_collection = {
                    ok: false,
                    skipped: !requireCollection,
                    error: 'Could not create or resolve collection via import',
                    attempts,
                };
                if (requireCollection) {
                    return {
                        ok: false,
                        error: `Could not create/find collection "${collectionQuery}" after import. Products were NOT uploaded (would be unlinked).`,
                        query: collectionQuery,
                        attempts,
                        created_collection,
                    };
                }
            }
        } else if (requireCollection) {
            return {
                ok: false,
                error: `Collection "${collectionQuery}" not found (create_collection_if_missing=false).`,
                query: collectionQuery,
            };
        } else {
            created_collection = {
                ok: false,
                skipped: true,
                error: 'Collection not found (create_collection_if_missing=false)',
                query: collectionQuery,
            };
        }
    } else if (collectionid) {
        const found = collectionQuery
            ? await findCollections({ query: collectionQuery, limit: 8 })
            : { collections: [] };
        collection =
            (found.collections || []).find((c) => String(c.collectionid) === collectionid) || {
                collectionid,
                title_en: String(args.collectionname || collectionQuery || '').trim(),
                title_ar: '',
            };
    }

    // Never return "ok" for a named collection without a resolved id — that orphans products.
    if (requireCollection && collectionQuery && !collectionid) {
        return {
            ok: false,
            error: `Collection "${collectionQuery}" could not be resolved to a collectionid. Products were not imported.`,
            query: collectionQuery,
            attempts,
            created_collection,
        };
    }

    if (collectionid && collection && !collection.title_en && collectionQuery) {
        collection = { ...collection, title_en: collectionQuery };
    }

    return { ok: true, collectionid, collection, created_collection, attempts };
}

function blankOrNum(value, fallback = '') {
    if (value === undefined || value === null || value === '') return fallback;
    return num(value);
}

/** Normalize image URLs into a comma-separated images_links string. */
function toImagesLinks(...sources) {
    const links = [];
    const push = (value) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
            value.forEach(push);
            return;
        }
        String(value)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((url) => {
                if (!links.includes(url)) links.push(url);
            });
    };
    sources.forEach(push);
    return links.join(',');
}

function firstImageLink(imagesLinks) {
    const first = String(imagesLinks || '')
        .split(',')
        .map((s) => s.trim())
        .find(Boolean);
    return first || '';
}

/** Stable unique sheet id for new catalog rows (import APIs need distinct ids). */
function catalogRowId(name, index) {
    const base = String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 36);
    return `${base || 'item'}-${index + 1}`;
}

/** Fetch one image URL via the Node AI server (never call Pexels from the browser). */
async function fetchAiImageUrl(query) {
    const q = String(query || '').trim();
    if (!q) return '';
    try {
        const data = await aiPost('/instowner/ai/pexels/search', { query: q, per_page: 5 });
        return (
            String(data.suggested_image_url || '').trim() ||
            String(data.photos?.[0]?.src?.large || data.photos?.[0]?.src?.medium || '').trim()
        );
    } catch (_) {
        return '';
    }
}

async function attachMissingProductImages(rows, withImages) {
    if (!withImages || !rows?.length) return rows;
    const missing = [];
    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (row.images_links || row.MainImageLink) {
            if (!row.MainImageLink && row.images_links) {
                row.MainImageLink = firstImageLink(row.images_links);
            }
            if (!row.images_links && row.MainImageLink) {
                row.images_links = row.MainImageLink;
            }
            continue;
        }
        missing.push(row);
    }
    const CONCURRENCY = 6;
    let cursor = 0;
    await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, missing.length) || 0 }, async () => {
            while (cursor < missing.length) {
                if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
                const i = cursor;
                cursor += 1;
                const row = missing[i];
                const query =
                    String(row.image_query || '').trim() ||
                    `${row.name_en || 'product'} studio product photography white background`;
                const url = await fetchAiImageUrl(query);
                if (url) {
                    row.images_links = url;
                    row.MainImageLink = url;
                }
            }
        }),
    );
    return rows;
}

function normalizeImportProduct(raw, index, collection, stamp) {
    const p = raw && typeof raw === 'object' ? raw : {};
    const collectionid = collection?.collectionid ? String(collection.collectionid) : '';
    const collectionname =
        String(p.collectionname || '').trim() ||
        collection?.title_en ||
        collection?.title_ar ||
        '';
    const name_en = String(p.name_en || p.name || '').trim();
    // Do NOT silently copy English into Arabic — empty name_ar must fail validation.
    const name_ar = String(p.name_ar || '').trim();
    const hasSale = String(p.has_sale ?? '0') === '1' ? '1' : '0';
    const productavail = String(p.productavail || 'Active').trim() === 'Draft' ? 'Draft' : 'Active';
    const quantavailtype = String(p.quantavailtype || 'nolimit').trim() === 'limit' ? 'limit' : 'nolimit';
    const sku =
        String(p.sku || '').trim() || `TX-${stamp}-${String(index + 1).padStart(3, '0')}`;

    const images_links = toImagesLinks(
        p.images_links,
        p.image_links,
        p.MainImageLink,
        p.mainimage,
        p.image_url,
    );
    const MainImageLink =
        String(p.MainImageLink || p.mainimage || '').trim() || firstImageLink(images_links);

    return {
        productid: '', // new products — leave empty so import creates them
        sku,
        name_en,
        name_ar,
        name_chin: String(p.name_chin || '').trim(),
        description_en: String(p.description_en || '').trim(),
        description_ar: String(p.description_ar || '').trim(),
        description_chin: String(p.description_chin || '').trim(),
        short_description_en: String(p.short_description_en || '').trim(),
        short_description_ar: String(p.short_description_ar || '').trim(),
        productavail,
        quantity: p.quantity === undefined || p.quantity === null || p.quantity === '' ? 10 : num(p.quantity),
        price: num(p.price),
        sale_price: hasSale === '1' ? num(p.sale_price) : blankOrNum(p.sale_price, 0),
        has_sale: hasSale,
        measurement_unit: String(p.measurement_unit || '').trim(),
        minimumproductquant: blankOrNum(p.minimumproductquant, 0),
        maximumproductquant: blankOrNum(p.maximumproductquant, 30000),
        maximumproductquantityperday: blankOrNum(p.maximumproductquantityperday, 10000),
        orderedquantity: blankOrNum(p.orderedquantity, 0),
        productcashbackvalue: blankOrNum(p.productcashbackvalue, 0),
        productindex: blankOrNum(p.productindex, ''),
        deliverydatetext: String(p.deliverydatetext || '').trim(),
        quantavailtype,
        productshippingfees: blankOrNum(p.productshippingfees, ''),
        cost_price: blankOrNum(p.cost_price, ''),
        vendorname: String(p.vendorname || '').trim(),
        vendorphone: String(p.vendorphone || '').trim(),
        vendorproviderid: String(p.vendorproviderid || '').trim(),
        productcanrate: blankOrNum(p.productcanrate, ''),
        isrequiredsubscription: blankOrNum(p.isrequiredsubscription, ''),
        is_unify_variants_quantity: blankOrNum(p.is_unify_variants_quantity, ''),
        prodaffpercentprofit: blankOrNum(p.prodaffpercentprofit, ''),
        lastquantityadded: blankOrNum(p.lastquantityadded, ''),
        tags: String(p.tags || '').trim(),
        refcollectionid: collectionid || String(p.refcollectionid || '').trim(),
        collectionname,
        MainImageLink,
        images_links,
        productsitelink: '', // never invent storefront URLs
    };
}

const ARABIC_SCRIPT_RE = /[\u0600-\u06FF]/;
const BLAND_PRODUCT_DESC_RE =
    /\b(a (functional|stylish|spacious|versatile|perfect|beautiful|elegant) .{0,40}(for|perfect for)|perfect for any occasion|everyday use|must[- ]have)\b/i;

const AR_PRODUCT_NAME_MAP = {
    bag: 'حقيبة',
    bags: 'حقائب',
    handbag: 'حقيبة يد',
    tote: 'حقيبة tote',
    backpack: 'حقيبة ظهر',
    crossbody: 'كروس بودي',
    clutch: 'كلاتش',
    laptop: 'لابتوب',
    leather: 'جلد',
    canvas: 'كانفاس',
    skirt: 'تنورة',
    skirts: 'تنانير',
    mini: 'ميني',
    midi: 'ميدي',
    maxi: 'ماكسي',
    pleated: 'بليسيه',
    'a-line': 'A-line',
    aline: 'A-line',
    skater: 'سكيتر',
    denim: 'دينم',
    silk: 'حرير',
    cotton: 'قطن',
    black: 'أسود',
    brown: 'بني',
    beige: 'بيج',
    white: 'أبيض',
    navy: 'كحلي',
};

function stripDescHtml(text) {
    return String(text || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function guessArabicProductName(nameEn) {
    const en = String(nameEn || '').trim();
    if (!en) return 'منتج';
    const parts = en.split(/\s+/).filter(Boolean);
    const mapped = parts.map((part) => {
        const key = part.toLowerCase().replace(/[^a-z0-9-]/g, '');
        return AR_PRODUCT_NAME_MAP[key] || null;
    });
    const arParts = mapped.filter(Boolean);
    if (arParts.length) {
        const rest = parts.filter((part, i) => !mapped[i]).join(' ');
        return rest ? `${arParts.join(' ')} ${rest}` : arParts.join(' ');
    }
    return `منتج ${en}`;
}

/** Auto-fill missing/bland bilingual copy so imports don't fail mid-flow. */
function polishImportProductCopy(row) {
    const nameEn = String(row.name_en || '').trim();
    if (!nameEn) return row;

    let nameAr = String(row.name_ar || '').trim();
    if (!nameAr || !ARABIC_SCRIPT_RE.test(nameAr) || nameAr.toLowerCase() === nameEn.toLowerCase()) {
        nameAr = guessArabicProductName(nameEn);
    }

    let descEn = stripDescHtml(row.description_en);
    if (descEn.length < 120 || BLAND_PRODUCT_DESC_RE.test(descEn)) {
        descEn =
            `Discover the ${nameEn}: designed with refined materials, clean lines, and everyday-ready comfort. Thoughtful details elevate the silhouette — from finishing and hardware to how it moves with you — so it feels as good as it looks. Style it for work, weekends, or nights out; it is built to become a wardrobe favorite.`;
    }

    let descAr = stripDescHtml(row.description_ar);
    if (!descAr || !ARABIC_SCRIPT_RE.test(descAr) || descAr.length < 80) {
        descAr =
            `اكتشفي ${nameAr}: قطعة بتصميم أنيق وخامات مختارة بعناية لراحة الاستخدام اليومي. تفاصيل التشطيب والقصة تمنحها مظهرًا راقيًا مع سهولة في التنسيق، فتناسب إطلالتك من الصباح حتى المساء. أضيفيها إلى خزانتك كاختيار أساسي يمنحك أناقة عملية في كل مناسبة.`;
    }

    let shortEn = stripDescHtml(row.short_description_en);
    if (!shortEn || shortEn.length < 20) {
        shortEn = `${nameEn} — refined materials, polished finish, everyday ease.`;
    }

    let shortAr = stripDescHtml(row.short_description_ar);
    if (!shortAr || !ARABIC_SCRIPT_RE.test(shortAr)) {
        shortAr = `${nameAr} — خامات أنيقة وتشطيب راقٍ لسهولة يومية.`;
    }

    row.name_ar = nameAr;
    row.description_en = descEn;
    row.description_ar = descAr;
    row.short_description_en = shortEn;
    row.short_description_ar = shortAr;
    return row;
}

/** Only hard-fail when English name is missing after polish. */
function validateImportProductCopy(row, index) {
    const nameEn = String(row.name_en || '').trim();
    if (!nameEn) {
        return { row: index + 1, sku: row.sku, name_en: '', issues: ['missing name_en'] };
    }
    const nameAr = String(row.name_ar || '').trim();
    const descEn = stripDescHtml(row.description_en);
    const descAr = stripDescHtml(row.description_ar);
    const issues = [];
    if (!nameAr || !ARABIC_SCRIPT_RE.test(nameAr)) issues.push('missing name_ar');
    if (descEn.length < 80) issues.push('description_en too short');
    if (!descAr || !ARABIC_SCRIPT_RE.test(descAr)) issues.push('missing description_ar');
    if (!issues.length) return null;
    return { row: index + 1, sku: row.sku, name_en: nameEn, issues };
}

async function generateProductsImportExcel(args = {}) {
    const productsIn = Array.isArray(args.products) ? args.products : [];
    const fromUserFile = args.user_file_import === true;
    const maxRows = fromUserFile ? USER_FILE_PRODUCT_MAX_ROWS : PRODUCT_IMPORT_MAX_ROWS;
    if (!productsIn.length) {
        return { ok: false, error: `products array is required (1–${maxRows} rows)` };
    }
    if (productsIn.length > maxRows) {
        return {
            ok: false,
            error: `Max ${maxRows} products per file. Split into another call.`,
            received: productsIn.length,
        };
    }

    // User-file rows are already enhanced on the server; still fill image gaps if asked.
    const wantImages = !(args.skip_images === true || args.without_images === true);
    const shouldUpload = args.upload !== false;
    const alsoDownload = fromUserFile ? args.also_download === true : args.also_download !== false;

    const resolved = await resolveCollectionForImport(args);
    if (!resolved.ok) {
        return resolved;
    }

    const collectionQuery = String(
        args.collection_query || args.collection || args.collectionname || '',
    ).trim();
    if (collectionQuery && !resolved.collectionid) {
        return {
            ok: false,
            error: `Refusing to import products for "${collectionQuery}" without a resolved collectionid (would not join the collection).`,
            attempts: resolved.attempts,
        };
    }

    const stamp = Date.now().toString(36).toUpperCase();
    const rows = [];
    const imageNotes = [];
    for (let i = 0; i < productsIn.length; i += 1) {
        if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const row = normalizeImportProduct(productsIn[i], i, resolved.collection, stamp);
        if (!row.name_en) {
            return { ok: false, error: `Row ${i + 1} is missing name_en` };
        }
        // Keep the query around for FE image fallback
        row.image_query = String(productsIn[i]?.image_query || productsIn[i]?.image_keywords || '').trim();

        // Force join: every row must carry the resolved collection id + name
        if (resolved.collectionid) {
            row.refcollectionid = String(resolved.collectionid);
            row.collectionname =
                resolved.collection?.title_en ||
                resolved.collection?.title_ar ||
                collectionQuery ||
                row.collectionname;
        }

        rows.push(row);
    }

    for (const row of rows) {
        if (fromUserFile) {
            const nameEn = String(row.name_en || '').trim();
            const nameAr = String(row.name_ar || '').trim();
            if (nameEn && (!nameAr || !ARABIC_SCRIPT_RE.test(nameAr))) {
                row.name_ar = guessArabicProductName(nameEn);
            }
            if (!stripDescHtml(row.description_en) || !stripDescHtml(row.description_ar)) {
                polishImportProductCopy(row);
            }
        } else {
            polishImportProductCopy(row);
        }
    }

    const copyErrors = rows.map((row, i) => validateImportProductCopy(row, i)).filter(Boolean);
    if (copyErrors.length && !fromUserFile) {
        return {
            ok: false,
            error:
                'Product copy incomplete after auto-polish: every product still needs name_en, name_ar, description_en, and description_ar.',
            copy_errors: copyErrors.slice(0, 12),
            note: 'Retry generate_products_import_excel with fuller EN+AR copy — do not ask the user to rewrite manually.',
        };
    }

    if (wantImages) {
        // Server usually already attached images; only fill gaps (avoids a second slow Pexels round).
        await attachMissingProductImages(rows, true);
    }

    for (const row of rows) {
        if (row.images_links || row.MainImageLink) {
            if (!row.images_links && row.MainImageLink) row.images_links = row.MainImageLink;
            if (!row.MainImageLink && row.images_links) row.MainImageLink = firstImageLink(row.images_links);
            imageNotes.push({
                sku: row.sku,
                images_links: row.images_links,
                MainImageLink: row.MainImageLink,
                from: 'server_or_ai_fallback',
            });
        } else if (wantImages) {
            imageNotes.push({
                sku: row.sku,
                error: 'no images_links after server + AI fallback',
            });
        }
        delete row.image_query;
    }

    const unlinked = rows.filter((r) => collectionQuery && !String(r.refcollectionid || '').trim());
    if (unlinked.length) {
        return {
            ok: false,
            error: `${unlinked.length} product row(s) missing refcollectionid — import aborted so products are not orphaned.`,
            collection_query: collectionQuery,
            collectionid: resolved.collectionid || null,
        };
    }

    const skus = rows.map((r) => r.sku);
    const uniqueSkus = new Set(skus);
    if (uniqueSkus.size !== skus.length) {
        return { ok: false, error: 'Duplicate SKUs in the products list — each sku must be unique' };
    }

    let filename = String(args.filename || 'products-import.xlsx').trim() || 'products-import.xlsx';
    if (!/\.xlsx$/i.test(filename)) {
        filename = `${filename}.xlsx`;
    }

    const aoa = [PRODUCT_IMPORT_HEADERS, ...rows.map((row) => PRODUCT_IMPORT_HEADERS.map((h) => row[h] ?? ''))];
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
    const xlsxArray = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new File([xlsxArray], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    if (alsoDownload) {
        XLSX.writeFile(workbook, filename);
    }

    let uploadResult = null;
    let uploadError = null;
    if (shouldUpload) {
        try {
            const currency = await fetchDefaultCurrency();
            const currencyid = currency.currencyid || currency.defaultcurrency || currency.id || '';
            if (!currencyid) {
                uploadError = 'No default currencyid — cannot call importproductsfile';
            } else {
                const form = new FormData();
                form.append('productfile', file);
                form.append('currencyid', String(currencyid));
                form.append('vendorid', String(args.vendorid || ''));
                form.append('quantitytype', String(args.quantitytype || 'setandclear'));
                form.append('updatevariantsprice', String(args.updatevariantsprice || 'no'));

                if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
                const response = await fetch(serverbaselink + '/instowner/importproductsfile', {
                    method: 'POST',
                    credentials: 'include',
                    body: form,
                    signal: toolAbortSignal || undefined,
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    uploadError = `importproductsfile HTTP ${response.status}`;
                    uploadResult = data;
                } else {
                    uploadResult = data;
                    if (!data.status) {
                        uploadError = data.reason || data.message || 'importproductsfile returned status=false';
                    }
                }
            }
        } catch (err) {
            if (err?.name === 'AbortError') throw err;
            uploadError = err.message || 'importproductsfile failed';
            uploadResult = { error: uploadError, status: err.status, data: err.data };
        }
    }

    const collectionTitle =
        resolved.collection?.title_en ||
        resolved.collection?.title_ar ||
        collectionQuery ||
        (resolved.collectionid ? String(resolved.collectionid) : null);

    const uploadedOk = shouldUpload ? Boolean(uploadResult?.status) && !uploadError : true;

    return {
        ok: uploadedOk || (!shouldUpload && alsoDownload),
        downloaded: alsoDownload,
        uploaded: shouldUpload && uploadedOk,
        filename,
        row_count: rows.length,
        images_attached: imageNotes.filter((n) => n.images_links || n.MainImageLink).length,
        schema: 'exportproducts',
        column_count: PRODUCT_IMPORT_HEADERS.length,
        collectionid: resolved.collectionid || null,
        collection_title: collectionTitle,
        collection_note: resolved.created_collection?.skipped
            ? `Collection skipped: ${resolved.created_collection.error}`
            : resolved.collectionid
              ? `Products linked to collectionid=${resolved.collectionid}${
                    resolved.created_collection?.via ? ` (created via ${resolved.created_collection.via})` : ''
                }`
              : null,
        created_collection: resolved.created_collection
            ? {
                  collectionid: resolved.created_collection.collectionid || resolved.collectionid || null,
                  skipped: Boolean(resolved.created_collection.skipped),
                  via: resolved.created_collection.via || null,
                  error: resolved.created_collection.error,
              }
            : null,
        collection_attempts: resolved.attempts || undefined,
        skus: skus.slice(0, 15),
        preview: rows.slice(0, 5).map((r) => ({
            sku: r.sku,
            name_en: r.name_en,
            name_ar: r.name_ar,
            description_en: String(r.description_en || '').slice(0, 140),
            description_ar: String(r.description_ar || '').slice(0, 140),
            price: r.price,
            sale_price: r.sale_price,
            has_sale: r.has_sale,
            MainImageLink: r.MainImageLink,
            images_links: r.images_links,
            refcollectionid: r.refcollectionid,
            collectionname: r.collectionname,
        })),
        image_notes: imageNotes.slice(0, 12),
        open_path: resolved.collectionid
            ? `/dashboard/institute/collections/editcollection/${resolved.collectionid}?section=products`
            : '/dashboard/institute/products/all/0',
        auto_open: uploadedOk,
        navigate: uploadedOk
            ? {
                  path: resolved.collectionid
                      ? `/dashboard/institute/collections/editcollection/${resolved.collectionid}?section=products`
                      : '/dashboard/institute/products/all/0',
                  label: collectionTitle || 'Products',
              }
            : null,
        error: uploadError || undefined,
        ...slimApi(
            {
                upload: shouldUpload,
                with_images: wantImages,
                row_count: rows.length,
                collectionid: resolved.collectionid || null,
            },
            uploadResult || { downloaded_only: true },
        ),
        note: shouldUpload
            ? uploadedOk
                ? `Imported ${rows.length} products via /instowner/importproductsfile${
                      imageNotes.some((n) => n.images_links || n.MainImageLink) ? ' with product images' : ''
                  }${
                      resolved.collectionid
                          ? ` linked to collection ${collectionTitle} (${resolved.collectionid})`
                          : ''
                  }.`
                : `Sheet built (${filename}) but upload failed: ${uploadError}. Do NOT invent a manual catalog — report this error.`
            : 'Sheet downloaded only (upload=false).',
    };
}

/** Keep API request/response for the model (truncated) so it can reason like support. */
function slimApi(request, response, maxChars = 14000) {
    try {
        const s = JSON.stringify(response);
        if (!s || s.length <= maxChars) {
            return { api_request: request || null, api_response: response };
        }
        return {
            api_request: request || null,
            api_response: { _truncated: true, length: s.length, preview_text: s.slice(0, maxChars) },
        };
    } catch (_) {
        return { api_request: request || null, api_response: { _error: 'unserializable' } };
    }
}

function asHtml(text) {
    const t = String(text || '').trim();
    if (!t) return '';
    if (/<[a-z][\s\S]*>/i.test(t)) return t;
    return `<p>${t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
}

function scoreNameMatch(hay, query) {
    const h = String(hay || '').toLowerCase().trim();
    const q = String(query || '').toLowerCase().trim();
    if (!q || !h) return 0;
    if (h === q) return 100;
    if (h.startsWith(q)) return 80;
    if (h.includes(q)) return 60;
    if (q.split(/\s+/).every((p) => h.includes(p))) return 40;
    return 0;
}

async function aiPost(path, body) {
    const response = await fetch(aiserverbaselink + path, {
        method: 'POST',
        headers: {
            ...jsonHeaders,
            ...aiInstituteHeaders(),
        },
        body: JSON.stringify(body || {}),
        signal: toolAbortSignal || undefined,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
        throw new ApiError(data.error || `AI ${path} failed (${response.status})`, {
            status: response.status,
            data,
            path,
        });
    }
    return data;
}

async function searchPexelsPhotos() {
    // Image search is Node-only. Browser must never call Pexels search.
    return {
        ok: false,
        error: 'Image search runs on the AI server only — not in the browser.',
        photos: [],
        suggested_image_url: null,
    };
}

/** Download an already-resolved image URL via the Node proxy (no search). */
async function downloadPexelsAsFile(imageUrl) {
    const data = await aiPost('/instowner/ai/pexels/download', { url: imageUrl });
    const bin = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0));
    return new File([bin], data.filename || 'collection-logo.jpg', {
        type: data.content_type || 'image/jpeg',
    });
}

async function listShippingZones(args = {}) {
    const request = {};
    const data = await postApi('/instowner/fetchinstshippingzones', request);
    const zones = data.zones || data.data?.zones || [];
    const query = String(args.query || '').trim().toLowerCase();
    const filtered = query
        ? zones
              .map((z) => ({
                  z,
                  score: Math.max(scoreNameMatch(z.zonename, query), scoreNameMatch(z.zonename_ar, query)),
              }))
              .filter((r) => r.score > 0)
              .sort((a, b) => b.score - a.score)
              .map((r) => r.z)
        : zones;

    return {
        ok: Boolean(data.status ?? true),
        count: filtered.length,
        zones: filtered.map((z) => ({
            zoneid: z.zoneid,
            zonename: z.zonename,
            zonerateprice: z.zonerateprice,
            zoneratesaleprice: z.zoneratesaleprice,
            hassale: z.hassale,
            country_id: z.country_id,
            state_id: z.state_id,
            zonenotes: z.zonenotes,
            cities_count: (z.zonecities || []).length,
            zonecities: z.zonecities || [],
            open_path: `/dashboard/institute/shipping/edit-zone/${z.zoneid}`,
        })),
        best_match: filtered[0]
            ? {
                  zoneid: filtered[0].zoneid,
                  zonename: filtered[0].zonename,
                  state_id: filtered[0].state_id,
                  country_id: filtered[0].country_id,
              }
            : null,
        ...slimApi(request, data),
        note: 'To add a missing city, prefer add_city_to_zone (it fetches cities then edits the zone).',
    };
}

async function fetchGeo(args = {}) {
    const level = String(args.level || '').trim();
    const query = String(args.query || '').trim();
    let path = '';
    let request = {};
    if (level === 'countries') {
        path = '/instowner/fetchcountries';
        request = {};
    } else if (level === 'states') {
        if (!args.country_id) return { ok: false, error: 'country_id is required for states' };
        path = '/instowner/fetchstates';
        request = { country_id: String(args.country_id) };
    } else if (level === 'cities') {
        if (!args.state_id) return { ok: false, error: 'state_id is required for cities' };
        path = '/instowner/fetchcities';
        request = { state_id: String(args.state_id) };
    } else {
        return { ok: false, error: 'level must be countries|states|cities' };
    }

    const data = await postApi(path, request);
    const key = level === 'countries' ? 'countries' : level === 'states' ? 'states' : 'cities';
    let rows = data[key] || data.data?.[key] || [];
    if (query) {
        const q = query.toLowerCase();
        rows = rows
            .map((row) => ({
                row,
                score: Math.max(scoreNameMatch(row.name, q), scoreNameMatch(row.name_ar, q), scoreNameMatch(row.name_en, q)),
            }))
            .filter((r) => r.score > 0)
            .sort((a, b) => b.score - a.score)
            .map((r) => r.row);
    }

    return {
        ok: Boolean(data.status ?? true),
        level,
        count: rows.length,
        best_match: rows[0] || null,
        [key]: rows.slice(0, 40).map((row) => ({
            id: row.id,
            name: row.name || row.name_en || '',
            name_ar: row.name_ar || '',
        })),
        ...slimApi(request, { status: data.status, [key]: rows.slice(0, 40) }),
    };
}

async function addCityToZone(args = {}) {
    const cityQuery = String(args.city_query || args.city || '').trim();
    const cityIdArg = String(args.city_id || args.cityid || '').trim();
    if (!cityQuery && !cityIdArg) {
        return { ok: false, error: 'city_query or city_id is required' };
    }

    const zonesData = await postApi('/instowner/fetchinstshippingzones', {});
    const zones = zonesData.zones || zonesData.data?.zones || [];
    let zone = null;
    if (args.zoneid) {
        zone = zones.find((z) => String(z.zoneid) === String(args.zoneid));
    }
    if (!zone && args.zone_query) {
        const q = String(args.zone_query).toLowerCase();
        const scored = zones
            .map((z) => ({ z, score: scoreNameMatch(z.zonename, q) }))
            .filter((r) => r.score > 0)
            .sort((a, b) => b.score - a.score);
        if (scored.length > 1 && scored[0].score < 95 && scored[0].score === scored[1].score) {
            return {
                ok: false,
                needs_choice: true,
                error: 'Multiple zones matched. Ask which zoneid.',
                zones: scored.slice(0, 8).map((r) => ({
                    zoneid: r.z.zoneid,
                    zonename: r.z.zonename,
                    score: r.score,
                })),
            };
        }
        zone = scored[0]?.z || null;
    }
    if (!zone && !args.zone_query && !args.zoneid) {
        // try infer from city later after we know state — for now require zone
        return { ok: false, error: 'zone_query or zoneid is required' };
    }
    if (!zone) {
        return { ok: false, error: 'Zone not found', zone_query: args.zone_query, zoneid: args.zoneid };
    }

    if (!zone.state_id) {
        return {
            ok: false,
            error: 'Zone has no state_id — cannot fetch cities',
            zoneid: zone.zoneid,
            zonename: zone.zonename,
        };
    }

    const citiesReq = { state_id: String(zone.state_id) };
    const citiesData = await postApi('/instowner/fetchcities', citiesReq);
    const cities = citiesData.cities || citiesData.data?.cities || [];

    let city = null;
    if (cityIdArg) {
        city = cities.find((c) => String(c.id) === cityIdArg) || null;
    }
    if (!city && cityQuery) {
        const scored = cities
            .map((c) => ({
                c,
                score: Math.max(scoreNameMatch(c.name, cityQuery), scoreNameMatch(c.name_ar, cityQuery)),
            }))
            .filter((r) => r.score > 0)
            .sort((a, b) => b.score - a.score);
        if (scored.length > 1 && scored[0].score < 90) {
            return {
                ok: false,
                needs_choice: true,
                error: 'Multiple cities matched. Ask which city id.',
                zoneid: zone.zoneid,
                zonename: zone.zonename,
                state_id: zone.state_id,
                cities: scored.slice(0, 10).map((r) => ({
                    id: r.c.id,
                    name: r.c.name,
                    name_ar: r.c.name_ar,
                    score: r.score,
                })),
                ...slimApi(citiesReq, { status: citiesData.status, matched: scored.slice(0, 10) }),
            };
        }
        city = scored[0]?.c || null;
    }

    if (!city) {
        return {
            ok: false,
            error: 'City not found in fetchcities for this zone state',
            zoneid: zone.zoneid,
            zonename: zone.zonename,
            state_id: zone.state_id,
            city_query: cityQuery,
            ...slimApi(citiesReq, { status: citiesData.status, cities_sample: cities.slice(0, 20) }),
        };
    }

    const existing = Array.isArray(zone.zonecities) ? [...zone.zonecities] : [];
    if (existing.some((c) => String(c.id) === String(city.id))) {
        return {
            ok: true,
            already_present: true,
            zoneid: zone.zoneid,
            zonename: zone.zonename,
            city: { id: city.id, name: city.name, name_ar: city.name_ar },
            note: 'City is already in this zone.',
            open_path: `/dashboard/institute/shipping/edit-zone/${zone.zoneid}`,
            navigate: {
                path: `/dashboard/institute/shipping/edit-zone/${zone.zoneid}`,
                label: zone.zonename || 'Zone',
            },
        };
    }

    const zonecities = [...existing, city];
    const savePayload = {
        functype: 'edit',
        zoneid: zone.zoneid,
        zonename: zone.zonename,
        country_id: zone.country_id || '',
        state_id: zone.state_id || '',
        zonenotes: zone.zonenotes || '',
        zonerateprice: zone.zonerateprice,
        hassale: zone.hassale ?? 0,
        zoneratesaleprice: zone.zoneratesaleprice,
        zonecities,
        zoneindex: zone.zoneindex || '',
    };
    const saveData = await postApi('/instowner/shippingzonescrud', savePayload);

    return {
        ok: Boolean(saveData.status ?? true),
        added: true,
        zoneid: zone.zoneid,
        zonename: zone.zonename,
        city: { id: city.id, name: city.name, name_ar: city.name_ar },
        cities_in_zone: zonecities.length,
        reason: saveData.reason,
        open_path: `/dashboard/institute/shipping/edit-zone/${zone.zoneid}`,
        navigate: {
            path: `/dashboard/institute/shipping/edit-zone/${zone.zoneid}`,
            label: zone.zonename || 'Zone',
        },
        ...slimApi(savePayload, saveData),
    };
}

async function getCollectionInfo(args = {}) {
    let collectionid = String(args.collectionid || '').trim();
    if (!collectionid && args.query) {
        const found = await findCollections({ query: args.query, limit: 8 });
        if (!found.ok || !found.best_match?.collectionid) {
            return { ok: false, error: 'Collection not found', ...found };
        }
        if ((found.collections || []).length > 1 && found.best_match.score < 95) {
            return {
                ok: false,
                needs_choice: true,
                error: 'Multiple collections matched. Ask which collectionid.',
                collections: found.collections,
            };
        }
        collectionid = String(found.best_match.collectionid);
    }
    if (!collectionid) return { ok: false, error: 'collectionid or query is required' };

    const request = { collectionid };
    const data = await postApi('/instowner/fetchcollectioninfo', request);
    const payload = data.data && typeof data.data === 'object' ? data.data : data;
    const info = payload.collectioninfo || payload.collection || payload;

    return {
        ok: Boolean(payload.status ?? data.status ?? true),
        collectionid,
        collection: {
            collectionid: info.collectionid || collectionid,
            title_en: info.title_en || '',
            title_ar: info.title_ar || '',
            description_en: info.description_en || '',
            description_ar: info.description_ar || '',
            collectionlogo: info.collectionlogo || '',
            collectionbanner: info.collectionbanner || '',
            isshowntocustomers: info.isshowntocustomers,
            isinftiler: info.isinftiler,
            products_count: Array.isArray(info.products) ? info.products.length : undefined,
        },
        open_path: `/dashboard/institute/collections/editcollection/${collectionid}?section=information`,
        ...slimApi(request, payload),
    };
}

/**
 * Create ONE collection.
 * Prefer collectioncrud when a server-provided image_url exists (download via Node proxy only).
 * Otherwise use importproductsgroups (never the dead /importcollectionsfile route).
 */
async function createCollection(args = {}) {
    const title_en = String(args.title_en || '').trim();
    const description_en = String(args.description_en || '').trim();
    if (!title_en || !description_en) {
        return { ok: false, error: 'title_en and description_en are required' };
    }

    const title_ar = String(args.title_ar || title_en).trim();
    const imageUrl = firstImageLink(toImagesLinks(args.image_url, args.images_links));

    // Path A: collectioncrud with logo (image URL already resolved on Node)
    if (imageUrl) {
        try {
            const file = await downloadPexelsAsFile(imageUrl);
            const form = new FormData();
            const requestMeta = {
                functype: 'add',
                title_en,
                title_ar,
                description_en: asHtml(description_en),
                description_ar: asHtml(args.description_ar || description_en),
                isshowntocustomers: String(args.isshowntocustomers ?? '1'),
                isinftiler: String(args.isinftiler ?? '1'),
                image_url: imageUrl,
            };
            form.append('functype', 'add');
            form.append('title_en', requestMeta.title_en);
            form.append('title_ar', requestMeta.title_ar);
            form.append('description_en', requestMeta.description_en);
            form.append('description_ar', requestMeta.description_ar);
            form.append('isshowntocustomers', requestMeta.isshowntocustomers);
            form.append('isinftiler', requestMeta.isinftiler);
            form.append('refinstid', '');
            form.append('products', JSON.stringify([]));
            form.append('vendors', JSON.stringify([]));
            form.append('collectionlogo', file);

            if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
            const response = await fetch(serverbaselink + '/instowner/collectioncrud', {
                method: 'POST',
                credentials: 'include',
                body: form,
                signal: toolAbortSignal || undefined,
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.status) {
                let resolvedId =
                    data.collectionid ||
                    data.data?.collectionid ||
                    data.collection?.collectionid ||
                    data.newcollectionid ||
                    null;
                if (!resolvedId) {
                    await sleep(700);
                    const found = await findCollectionExactOrBest(title_en, 5);
                    resolvedId = found.best_match?.collectionid || null;
                }
                const id = resolvedId ? String(resolvedId) : null;
                return {
                    ok: Boolean(id),
                    created: true,
                    collectionid: id,
                    title_en,
                    title_ar,
                    via: 'collectioncrud',
                    open_path: id
                        ? `/dashboard/institute/collections/editcollection/${id}?section=information`
                        : '/dashboard/institute/collections',
                    navigate: {
                        path: id
                            ? `/dashboard/institute/collections/editcollection/${id}?section=information`
                            : '/dashboard/institute/collections',
                        label: title_en,
                    },
                    ...slimApi(requestMeta, data),
                    note: id
                        ? 'Collection created via collectioncrud. For 2+ collections use import_catalog_groups (importproductsgroups).'
                        : 'Create returned status=true but no collectionid — try find_collections.',
                };
            }
        } catch (err) {
            if (err?.name === 'AbortError') throw err;
            // fall through to import
        }
    }

    // Path B: importproductsgroups (works on tabapv; importcollectionsfile is 404 there)
    const imported = await createCollectionViaImport(
        title_en,
        title_ar,
        description_en,
        args.description_ar || description_en,
        args.image_query || `${title_en} fashion collection photography`,
        imageUrl,
    );

    await sleep(700);
    const found = await findCollectionExactOrBest(title_en, 8);
    const resolvedId = found.best_match?.collectionid
        ? String(found.best_match.collectionid)
        : null;

    return {
        ok: Boolean(imported.ok || imported.imported) && Boolean(resolvedId || imported.ok),
        created: Boolean(imported.ok || imported.imported),
        collectionid: resolvedId,
        title_en,
        title_ar,
        via: 'importproductsgroups',
        reason: imported.reason || imported.error,
        open_path: resolvedId
            ? `/dashboard/institute/collections/editcollection/${resolvedId}?section=information`
            : '/dashboard/institute/collections',
        navigate: {
            path: resolvedId
                ? `/dashboard/institute/collections/editcollection/${resolvedId}?section=information`
                : '/dashboard/institute/collections',
            label: title_en,
        },
        ...slimApi(
            { title_en, title_ar, via: 'importproductsgroups', images_links: imageUrl },
            imported,
        ),
        note: resolvedId
            ? 'Collection created via importproductsgroups. For 2+ collections prefer one import_catalog_groups call.'
            : 'Import ran but collectionid not found yet — retry find_collections.',
    };
}

/** Shared import sheet for collection / parentcollection / category exports. */
const CATALOG_GROUP_HEADERS = [
    'NUid',
    'id',
    'name_en',
    'name_ar',
    'description_en',
    'description_ar',
    'showtocustomers',
    'showinfilter',
    'skiplayeronclick',
    'parentindex',
    'refparentid',
    'images_links',
];

const CATALOG_GROUP_MAX = 200;

function normalizeCatalogGroupRow(raw, index) {
    const p = raw && typeof raw === 'object' ? raw : {};
    const name_en = String(p.name_en || p.title_en || p.name || '').trim();
    const name_ar = String(p.name_ar || p.title_ar || '').trim() || name_en;
    const flag = (v, fallback) => {
        if (v === undefined || v === null || v === '') return fallback;
        return Number(v) ? 1 : 0;
    };
    const images_links = firstImageLink(
        toImagesLinks(p.images_links, p.image_links, p.image_url, p.MainImageLink),
    );
    return {
        // New rows need a unique sheet `id` so multi-collection imports don't collapse.
        // NUid stays empty for create; filled only when updating an existing system id.
        NUid: String(p.NUid || p.nuid || '').trim(),
        id: String(p.id || '').trim() || catalogRowId(name_en, index),
        name_en,
        name_ar,
        description_en: asHtml(p.description_en || ''),
        description_ar: asHtml(p.description_ar || p.description_en || ''),
        showtocustomers: flag(p.showtocustomers ?? p.isshowntocustomers, 1),
        showinfilter: flag(p.showinfilter ?? p.isinftiler, 1),
        skiplayeronclick: flag(p.skiplayeronclick, 0),
        parentindex: p.parentindex === undefined || p.parentindex === null || p.parentindex === '' ? index : num(p.parentindex),
        refparentid: String(p.refparentid || '').trim(),
        // Catalog groups: exactly one image URL in images_links
        images_links,
        _index: index,
        _image_query: String(p.image_query || p.image_keywords || '').trim(),
    };
}

async function importCatalogGroups(args = {}) {
    const grouptype = String(args.grouptype || '').trim();
    if (!['collection', 'parentcollection', 'category'].includes(grouptype)) {
        return { ok: false, error: 'grouptype must be collection | parentcollection | category' };
    }

    const itemsIn = Array.isArray(args.items) ? args.items : [];
    if (!itemsIn.length) {
        return { ok: false, error: 'items array is required' };
    }
    if (itemsIn.length > CATALOG_GROUP_MAX) {
        return { ok: false, error: `Max ${CATALOG_GROUP_MAX} rows per import`, received: itemsIn.length };
    }

    const wantImages = !(args.skip_images === true || args.without_images === true);
    const rows = [];
    for (let i = 0; i < itemsIn.length; i += 1) {
        const row = normalizeCatalogGroupRow(itemsIn[i], i);
        if (!row.name_en) {
            return { ok: false, error: `Row ${i + 1} is missing name_en` };
        }
        // Ensure images unless user asked without images
        if (wantImages && !row.images_links) {
            const query =
                row._image_query ||
                `${row.name_en} ${grouptype === 'category' ? 'category' : 'collection'} fashion photography`;
            row.images_links = await fetchAiImageUrl(query);
        }
        if (!wantImages) {
            row.images_links = row.images_links || '';
        }
        delete row._image_query;
        delete row._index;
        rows.push(row);
    }

    let filename = String(args.filename || `${grouptype}-import.xlsx`).trim() || `${grouptype}-import.xlsx`;
    if (!/\.xlsx$/i.test(filename)) filename = `${filename}.xlsx`;

    const aoa = [
        CATALOG_GROUP_HEADERS,
        ...rows.map((row) => CATALOG_GROUP_HEADERS.map((h) => (row[h] === undefined || row[h] === null ? '' : row[h]))),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, grouptype);
    const xlsxArray = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new File([xlsxArray], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const alsoDownload = args.also_download !== false;
    if (alsoDownload) {
        XLSX.writeFile(workbook, filename);
    }

    const form = new FormData();
    // tabapv.site has no /importcollectionsfile (404). Collections use the same
    // importproductsgroups endpoint as parent collections / categories.
    const uploadPath = '/instowner/importproductsgroups';
    form.append('grouptype', grouptype);
    form.append('productgroupsfile', file);

    if (toolAbortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await fetch(serverbaselink + uploadPath, {
        method: 'POST',
        credentials: 'include',
        body: form,
        signal: toolAbortSignal || undefined,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new ApiError(`Import ${uploadPath} failed (${response.status})`, {
            status: response.status,
            data,
            path: uploadPath,
        });
    }

    const openPaths = {
        collection: '/dashboard/institute/collections',
        parentcollection: '/dashboard/institute/parentcollections',
        category: '/dashboard/institute/categories',
    };

    return {
        ok: Boolean(data.status ?? true),
        imported: Boolean(data.status),
        grouptype,
        row_count: rows.length,
        filename,
        downloaded: alsoDownload,
        upload_path: uploadPath,
        reason: data.reason,
        preview: rows.slice(0, 8).map((r) => ({
            name_en: r.name_en,
            name_ar: r.name_ar,
            showtocustomers: r.showtocustomers,
            refparentid: r.refparentid,
            images_links: r.images_links,
        })),
        images_attached: rows.filter((r) => r.images_links).length,
        open_path: openPaths[grouptype],
        navigate: { path: openPaths[grouptype], label: grouptype },
        ...slimApi(
            {
                grouptype,
                path: uploadPath,
                row_count: rows.length,
                headers: CATALOG_GROUP_HEADERS,
                sample: rows.slice(0, 3),
            },
            data,
        ),
        note: data.status
            ? `Imported ${rows.length} ${grouptype} row(s) via ${uploadPath}. Prefer this over create_collection loops.`
            : 'Import failed — see api_response.reason',
    };
}

const USER_FILE_KINDS = ['product', 'collection', 'parentcollection', 'category'];

/**
 * Upload rows Tabexawy already mapped from a user Excel/CSV/PDF/Word file.
 * Does not invent catalog items — it only writes the Tabex import sheet and posts it.
 */
export async function importUserCatalogRows(args = {}) {
    const prevSignal = toolAbortSignal;
    if (args.signal) toolAbortSignal = args.signal;
    try {
        return await importUserCatalogRowsInner(args);
    } finally {
        toolAbortSignal = prevSignal;
    }
}

async function importUserCatalogRowsInner(args = {}) {
    const kind = String(args.kind || '').trim();
    if (!USER_FILE_KINDS.includes(kind)) {
        return { ok: false, error: 'kind must be product, collection, parentcollection, or category' };
    }
    const rows = Array.isArray(args.rows) ? args.rows : [];
    if (!rows.length) {
        return { ok: false, error: 'No rows to import after converting the file' };
    }

    const hasImages = rows.every(
        (row) => String(row?.images_links || '').trim() || String(row?.MainImageLink || '').trim(),
    );

    if (kind === 'product') {
        return generateProductsImportExcel({
            products: rows,
            collection_query: args.collection_query || '',
            create_collection_if_missing: Boolean(args.collection_query),
            require_collection: Boolean(args.collection_query),
            skip_images: hasImages,
            upload: true,
            also_download: args.also_download === true,
            filename: args.filename || 'products-from-file.xlsx',
            user_file_import: true,
        });
    }

    return importCatalogGroups({
        grouptype: kind,
        items: rows,
        skip_images: hasImages,
        filename: args.filename || `${kind}-from-file.xlsx`,
        also_download: args.also_download === true,
    });
}


const TOOL_RUNNERS = {
    get_orders: getOrders,
    get_order_info: getOrderInfo,
    get_products: getProducts,
    get_reservations: getReservations,
    get_customers: getCustomers,
    find_customers: findCustomers,
    get_customer_info: getCustomerInfo,
    create_customer: createCustomer,
    get_sales_report: getSalesReport,
    get_pages_analytics: getPagesAnalytics,
    get_default_currency: fetchDefaultCurrency,
    find_collections: findCollections,
    find_products: findProducts,
    get_product_info: getProductInfo,
    update_product_info: updateProductInfo,
    update_product_price: updateProductPrice,
    update_product_quantity: updateProductQuantity,
    list_bulk_products: listBulkProducts,
    bulk_update_products: bulkUpdateProducts,
    apply_product_discount: applyProductDiscount,
    generate_products_import_excel: generateProductsImportExcel,
    // Image search runs only on the Node server — never expose to the chat UI.
    list_shipping_zones: listShippingZones,
    fetch_geo: fetchGeo,
    add_city_to_zone: addCityToZone,
    create_collection: createCollection,
    import_catalog_groups: importCatalogGroups,
    get_collection_info: getCollectionInfo,
};

function parseArgs(rawArgs) {
    if (typeof rawArgs !== 'string') return rawArgs || {};
    try {
        return JSON.parse(rawArgs || '{}');
    } catch {
        return {};
    }
}

function openPage(args = {}) {
    const path = String(args.path || '').trim();
    const label = String(args.label || path).trim();
    if (
        !path ||
        path === '/' ||
        /^https?:\/\//i.test(path) ||
        !path.startsWith('/dashboard/institute')
    ) {
        return {
            ok: false,
            error: 'Path must be a relative dashboard route starting with /dashboard/institute (not / or a full URL).',
        };
    }
    return {
        ok: true,
        opened: true,
        path,
        label,
        navigate: { path, label },
    };
}

async function logoutUser() {
    const response = await fetch(`${serverbaselink}/generalall/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({}),
        signal: toolAbortSignal || undefined,
    });

    // Mirror header Logout: leave the dashboard even if the API is flaky
    setTimeout(() => {
        window.location.href = '/';
    }, 120);

    if (!response.ok) {
        let data = null;
        try {
            data = await response.json();
        } catch (_) {
            /* ignore */
        }
        return {
            ok: true,
            logged_out: true,
            redirected: true,
            warning: data?.message || `Logout API returned ${response.status}; redirecting anyway.`,
        };
    }

    return { ok: true, logged_out: true, redirected: true };
}

export async function executeDashboardTool(name, rawArgs) {
    const args = parseArgs(rawArgs);

    if (name === 'open_page') {
        return openPage(args);
    }

    if (name === 'logout') {
        return logoutUser();
    }

    const runner = TOOL_RUNNERS[name];
    if (!runner) {
        return { ok: false, error: `Unknown tool: ${name}` };
    }

    try {
        return await runner(args || {});
    } catch (err) {
        return {
            ok: false,
            error: err.message || 'Tool failed',
            status: err.status,
            validation_errors: err.data?.errors,
            api_message: err.data?.message,
        };
    }
}

function navigateFromToolResult(content, onNavigate) {
    if (!onNavigate || !content?.ok) return;
    const path = content.navigate?.path || content.path || (content.auto_open ? content.open_path : null);
    if (!path) return;
    onNavigate(path, content.navigate?.label || content.label || path);
}

export async function executeToolCalls(toolCalls, { onNavigate, signal } = {}) {
    const results = [];
    toolAbortSignal = signal || null;
    try {
        // Merge parallel update_product_info calls into one save so EN/AR don't overwrite each other.
        const mergedUpdates = [];
        const otherCalls = [];
        for (const call of toolCalls || []) {
            if (call.name === 'update_product_info') {
                mergedUpdates.push(call);
            } else {
                otherCalls.push(call);
            }
        }

        if (mergedUpdates.length) {
            if (signal?.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }
            const mergedArgs = {};
            for (const call of mergedUpdates) {
                Object.assign(mergedArgs, parseArgs(call.arguments));
            }
            const content = await updateProductInfo(mergedArgs);
            navigateFromToolResult(content, onNavigate);
            const payload = JSON.stringify(content);
            for (let i = 0; i < mergedUpdates.length; i += 1) {
                results.push({
                    tool_call_id: mergedUpdates[i].id,
                    content:
                        i === 0
                            ? payload
                            : JSON.stringify({
                                  ok: content.ok,
                                  merged: true,
                                  note: 'Merged into a single product update with the other fields from this turn.',
                                  productid: content.productid,
                                  updated: content.updated,
                                  ask_short_description: content.ask_short_description,
                                  open_path: content.open_path,
                              }),
                });
            }
        }

        // Rankings already include names from getPagesAnalytics — never fan out fetchproductinfo.
        const hasPagesAnalytics = otherCalls.some((c) => c.name === 'get_pages_analytics');
        const PRODUCT_FETCH_TOOLS = new Set(['get_product_info', 'get_products', 'find_products']);
        const PRODUCT_INFO_MAX = 3;
        let productInfoSeen = 0;
        const cappedCalls = [];
        const skippedProductInfo = [];
        for (const call of otherCalls) {
            if (hasPagesAnalytics && PRODUCT_FETCH_TOOLS.has(call.name)) {
                skippedProductInfo.push(call);
                continue;
            }
            if (call.name === 'get_product_info') {
                productInfoSeen += 1;
                if (productInfoSeen > PRODUCT_INFO_MAX) {
                    skippedProductInfo.push(call);
                    continue;
                }
            }
            cappedCalls.push(call);
        }

        // One Fetch_Order_Items even if the model asked get_reservations twice.
        const reservationCalls = cappedCalls.filter((c) => c.name === 'get_reservations');
        const otherCapped = cappedCalls.filter((c) => c.name !== 'get_reservations');
        let reservationContent = null;
        if (reservationCalls.length) {
            if (signal?.aborted) {
                throw new DOMException('Aborted', 'AbortError');
            }
            reservationContent = await executeDashboardTool(
                'get_reservations',
                reservationCalls[0].arguments,
            );
        }

        // Run independent tools in parallel (e.g. Bags + Skirts product imports).
        const parallel = await Promise.all(
            otherCapped.map(async (call) => {
                if (signal?.aborted) {
                    throw new DOMException('Aborted', 'AbortError');
                }
                const content = await executeDashboardTool(call.name, call.arguments);
                return { call, content };
            }),
        );
        if (reservationContent) {
            navigateFromToolResult(reservationContent, onNavigate);
            for (const call of reservationCalls) {
                results.push({
                    tool_call_id: call.id,
                    content: JSON.stringify(reservationContent),
                });
            }
        }
        for (const { call, content } of parallel) {
            navigateFromToolResult(content, onNavigate);
            results.push({
                tool_call_id: call.id,
                content: JSON.stringify(content),
            });
        }
        for (const call of skippedProductInfo) {
            const skippedForAnalytics =
                hasPagesAnalytics && PRODUCT_FETCH_TOOLS.has(call.name);
            results.push({
                tool_call_id: call.id,
                content: JSON.stringify({
                    ok: false,
                    skipped: true,
                    error: skippedForAnalytics
                        ? 'Refused: get_pages_analytics already has top_customers / top_ordered_items / top_visited_products names and counts. Answer from that JSON — do not call get_product_info.'
                        : `Refused: max ${PRODUCT_INFO_MAX} get_product_info calls per turn. Use find_products or ask for one productid.`,
                }),
            });
        }

        // Preserve original tool_call order for the model
        const byId = new Map(results.map((r) => [r.tool_call_id, r]));
        return (toolCalls || []).map((call) => byId.get(call.id)).filter(Boolean);
    } finally {
        toolAbortSignal = null;
    }
}
